import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Award,
  Baby,
  BellRing,
  BookOpen,
  Check,
  Clapperboard,
  Eraser,
  Gamepad2,
  Heart,
  Home,
  Instagram,
  Maximize,
  Mic2,
  Moon,
  Music,
  PawPrint,
  Pause,
  Paintbrush,
  Palette,
  Play,
  RefreshCw,
  ShieldCheck,
  Star,
  Volume2,
} from 'lucide-react';
import './App.css';

import { arabicLetters } from './data/arabicLetters';
import { animalCharacters, animalMatchRounds, countAnimalRounds, feedingPairs, familySingAlongVerses, familySongFinale } from './data/animalAdventure';
import { games } from './data/games';
import { nasheeds } from './data/nasheeds';
import { prophets } from './data/prophets';
import { surahs } from './data/surahs';
import { videos } from './data/videos';
import { useExplorerProgress } from './hooks/useExplorerProgress';
import { trackScreenView, trackEvent } from './services/analyticsService';
import animalBird from './assets/animal-adventure/bird.png';
import animalCamel from './assets/animal-adventure/camel.png';
import animalCat from './assets/animal-adventure/cat.png';
import animalCow from './assets/animal-adventure/cow.png';
import animalDuck from './assets/animal-adventure/duck.png';
import animalLamb from './assets/animal-adventure/lamb.png';
import animalLion from './assets/animal-adventure/lion.png';
import animalMonkey from './assets/animal-adventure/monkey.png';
import animalRabbit from './assets/animal-adventure/rabbit.png';
import animalTurtle from './assets/animal-adventure/turtle.png';

const sections = [
  { id: 'home', label: 'Home', color: 'sunset', Icon: Home },
  { id: 'parents', label: 'For Parents', color: 'plum', Icon: Heart },
  { id: 'play', label: 'Play', color: 'mint', Icon: Gamepad2 },
  { id: 'animals', label: 'Toddler Section', color: 'garden', Icon: PawPrint },
  { id: 'quran', label: 'Quran', color: 'ocean', Icon: BookOpen },
  { id: 'arabic', label: 'Arabic', color: 'berry', Icon: Baby },
  { id: 'words', label: 'Words', color: 'teal', Icon: Volume2 },
  { id: 'prayer', label: 'How to Pray', color: 'leaf', Icon: ShieldCheck },
  { id: 'prophets', label: 'Prophets', color: 'gold', Icon: Moon },
  { id: 'create', label: 'Create', color: 'paint', Icon: Palette },
  { id: 'nasheeds', label: 'Nasheeds', color: 'rose', Icon: Music },
  { id: 'videos', label: 'Videos', color: 'sky', Icon: Clapperboard },
];

const AGE_GROUP_KEY = 'little_muslim_age_group';
const under6SectionIds = ['animals', 'words', 'quran', 'prayer', 'create', 'nasheeds', 'videos', 'parents'];

const feedbackFormUrl = 'https://docs.google.com/forms/d/e/1FAIpQLScuNvR4RerinkWwX53A2R-mLq4baVR2ZJ44QHJL2hzm3tWhhA/viewform?usp=publish-editor';
const instagramUrl = 'https://www.instagram.com/the_little_muslim_explorer?igsh=MWIweHo0dzBpbDg5dw==';

const mobileLearningReminders = [
  {
    id: 'alphabet',
    title: 'Arabic time?',
    message: 'Did you study the alphabet today, little buddy?',
    actionLabel: 'Practise letters',
    sectionId: 'arabic',
  },
  {
    id: 'drawing',
    title: 'Create something',
    message: 'Why not try drawing a lovely picture today?',
    actionLabel: 'Open Create',
    sectionId: 'create',
  },
  {
    id: 'quran',
    title: 'Quran moment',
    message: 'Read or listen to a short surah today.',
    actionLabel: 'Open Quran',
    sectionId: 'quran',
  },
  {
    id: 'prayer',
    title: 'Prayer practice',
    message: 'Can you practise one salah movement with a grown-up?',
    actionLabel: 'How to Pray',
    sectionId: 'prayer',
  },
  {
    id: 'words',
    title: 'Beautiful words',
    message: 'Say Bismillah, Alhamdulillah, or MashaAllah today.',
    actionLabel: 'Hear words',
    sectionId: 'words',
  },
  {
    id: 'toddler',
    title: 'Tiny adventure',
    message: 'Can you count animals or find Lulu the Lamb?',
    actionLabel: 'Toddler Section',
    sectionId: 'animals',
  },
];

const colouringPages = [
  { id: 'kaaba', title: 'Kaaba' },
  { id: 'mosque', title: 'Mosque' },
  { id: 'mat', title: 'Prayer Mat' },
  { id: 'moon', title: 'Moon' },
  { id: 'stars', title: 'Stars' },
  { id: 'letters', title: 'Arabic Letters' },
  { id: 'lantern', title: 'Lantern' },
  { id: 'dates', title: 'Dates' },
  { id: 'tasbih', title: 'Prayer Beads' },
];

const soundButtons = [
  { id: 'assalamu-alaikum', label: 'Assalamu Alaikum', spoken: 'السَّلَامُ عَلَيْكُمْ', meaning: 'Peace be upon you' },
  { id: 'bismillah', label: 'Bismillah', spoken: 'بِسْمِ الله', meaning: 'In the name of Allah' },
  { id: 'alhamdulillah', label: 'Alhamdulillah', spoken: 'الْحَمْدُ لِلّٰهِ', meaning: 'All praise is for Allah' },
  { id: 'subhanallah', label: 'SubhanAllah', spoken: 'سُبْحَانَ الله', meaning: 'Glory be to Allah' },
  { id: 'allahu-akbar', label: 'Allahu Akbar', spoken: 'اللهُ أَكْبَر', meaning: 'Allah is the Greatest' },
  { id: 'jazakallahu-khair', label: 'JazakAllahu Khair', spoken: 'جَزَاكَ اللهُ خَيْرًا', meaning: 'May Allah reward you with good' },
  { id: 'mashaallah', label: 'MashaAllah', spoken: 'مَا شَاءَ الله', meaning: 'What Allah has willed' },
];

const simpleWords = [
  {
    id: 'bismillah',
    english: 'Bismillah',
    arabic: 'بِسْمِ الله',
    meaning: 'In the name of Allah',
    when: 'Say it before you begin.',
  },
  {
    id: 'alhamdulillah',
    english: 'Alhamdulillah',
    arabic: 'الْحَمْدُ لِلّٰهِ',
    meaning: 'All praise is for Allah',
    when: 'Say it when you feel thankful.',
  },
  {
    id: 'subhanallah',
    english: 'SubhanAllah',
    arabic: 'سُبْحَانَ الله',
    meaning: 'Glory be to Allah',
    when: 'Say it when you see something amazing.',
  },
  {
    id: 'allahu-akbar',
    english: 'Allahu Akbar',
    arabic: 'اللهُ أَكْبَر',
    meaning: 'Allah is the Greatest',
    when: 'Say it to remember Allah is greater than everything.',
  },
  {
    id: 'mashallah',
    english: 'MashaAllah',
    arabic: 'مَا شَاءَ الله',
    meaning: 'What Allah has willed',
    when: 'Say it when you see something lovely.',
  },
  {
    id: 'jazakallah-khair',
    english: 'JazakAllahu Khair',
    arabic: 'جَزَاكَ اللهُ خَيْرًا',
    meaning: 'May Allah reward you with good',
    when: 'Say it when someone helps you.',
  },
  {
    id: 'assalamu-alaikum',
    english: 'Assalamu Alaikum',
    arabic: 'السَّلَامُ عَلَيْكُمْ',
    meaning: 'Peace be upon you',
    when: 'Say it when you greet someone.',
  },
  {
    id: 'inshallah',
    english: 'InshaAllah',
    arabic: 'إِنْ شَاءَ الله',
    meaning: 'If Allah wills',
    when: 'Say it when you talk about a plan.',
  },
  {
    id: 'astaghfirullah',
    english: 'Astaghfirullah',
    arabic: 'أَسْتَغْفِرُ الله',
    meaning: 'I ask Allah for forgiveness',
    when: 'Say it after a mistake.',
  },
  {
    id: 'la-ilaha-illallah',
    english: 'La ilaha illa Allah',
    arabic: 'لَا إِلٰهَ إِلَّا الله',
    meaning: 'There is no god but Allah',
    when: 'Say it to remember tawheed.',
  },
];

const prophetStorySources = [
  {
    name: 'Adam',
    sources: [
      { label: 'Quran 2:30-39', url: 'https://quran.com/2/30-39' },
      { label: 'Quran 7:11-27', url: 'https://quran.com/7/11-27' },
      { label: 'Quran 20:115-123', url: 'https://quran.com/20/115-123' },
    ],
  },
  {
    name: 'Idris',
    sources: [
      { label: 'Quran 19:56-57', url: 'https://quran.com/19/56-57' },
      { label: 'Quran 21:85-86', url: 'https://quran.com/21/85-86' },
    ],
  },
  {
    name: 'Nuh',
    sources: [
      { label: 'Quran 11:25-49', url: 'https://quran.com/11/25-49' },
      { label: 'Quran 23:23-30', url: 'https://quran.com/23/23-30' },
      { label: 'Quran 71', url: 'https://quran.com/71' },
    ],
  },
  {
    name: 'Hud',
    sources: [
      { label: 'Quran 7:65-72', url: 'https://quran.com/7/65-72' },
      { label: 'Quran 11:50-60', url: 'https://quran.com/11/50-60' },
      { label: 'Quran 26:123-140', url: 'https://quran.com/26/123-140' },
    ],
  },
  {
    name: 'Salih',
    sources: [
      { label: 'Quran 7:73-79', url: 'https://quran.com/7/73-79' },
      { label: 'Quran 11:61-68', url: 'https://quran.com/11/61-68' },
      { label: 'Quran 26:141-159', url: 'https://quran.com/26/141-159' },
    ],
  },
  {
    name: 'Ibrahim',
    sources: [
      { label: 'Quran 6:74-83', url: 'https://quran.com/6/74-83' },
      { label: 'Quran 21:51-73', url: 'https://quran.com/21/51-73' },
      { label: 'Quran 37:83-113', url: 'https://quran.com/37/83-113' },
      { label: 'Quran 2:124-129', url: 'https://quran.com/2/124-129' },
      { label: 'Quran 51:24-30', url: 'https://quran.com/51/24-30' },
    ],
  },
  {
    name: 'Lut',
    sources: [
      { label: 'Quran 7:80-84', url: 'https://quran.com/7/80-84' },
      { label: 'Quran 11:77-83', url: 'https://quran.com/11/77-83' },
      { label: 'Quran 26:160-175', url: 'https://quran.com/26/160-175' },
    ],
  },
  {
    name: 'Ismail',
    sources: [
      { label: 'Quran 14:37-41', url: 'https://quran.com/14/37-41' },
      { label: 'Quran 2:125-129', url: 'https://quran.com/2/125-129' },
      { label: 'Quran 37:100-113', url: 'https://quran.com/37/100-113' },
      { label: 'Sahih al-Bukhari 3364', url: 'https://sunnah.com/bukhari:3364' },
    ],
  },
  {
    name: 'Ishaq',
    sources: [
      { label: 'Quran 11:69-73', url: 'https://quran.com/11/69-73' },
      { label: 'Quran 37:112-113', url: 'https://quran.com/37/112-113' },
      { label: 'Quran 6:84-87', url: 'https://quran.com/6/84-87' },
    ],
  },
  {
    name: 'Yaqub',
    sources: [
      { label: 'Quran 12:83-100', url: 'https://quran.com/12/83-100' },
      { label: 'Quran 2:132-133', url: 'https://quran.com/2/132-133' },
      { label: 'Quran 6:84-87', url: 'https://quran.com/6/84-87' },
    ],
  },
  {
    name: 'Yusuf',
    sources: [
      { label: 'Quran 12', url: 'https://quran.com/12' },
    ],
  },
  {
    name: 'Ayyub',
    sources: [
      { label: 'Quran 21:83-84', url: 'https://quran.com/21/83-84' },
      { label: 'Quran 38:41-44', url: 'https://quran.com/38/41-44' },
    ],
  },
  {
    name: 'Shuayb',
    sources: [
      { label: 'Quran 7:85-93', url: 'https://quran.com/7/85-93' },
      { label: 'Quran 11:84-95', url: 'https://quran.com/11/84-95' },
      { label: 'Quran 26:176-191', url: 'https://quran.com/26/176-191' },
    ],
  },
  {
    name: 'Musa',
    sources: [
      { label: 'Quran 20:9-98', url: 'https://quran.com/20/9-98' },
      { label: 'Quran 26:10-68', url: 'https://quran.com/26/10-68' },
      { label: 'Quran 28:3-46', url: 'https://quran.com/28/3-46' },
    ],
  },
  {
    name: 'Harun',
    sources: [
      { label: 'Quran 20:29-36', url: 'https://quran.com/20/29-36' },
      { label: 'Quran 28:34', url: 'https://quran.com/28/34' },
      { label: 'Quran 20:90-94', url: 'https://quran.com/20/90-94' },
      { label: 'Quran 7:142-151', url: 'https://quran.com/7/142-151' },
    ],
  },
  {
    name: 'Yunus',
    sources: [
      { label: 'Quran 21:87-88', url: 'https://quran.com/21/87-88' },
      { label: 'Quran 37:139-148', url: 'https://quran.com/37/139-148' },
      { label: 'Quran 10:98', url: 'https://quran.com/10/98' },
    ],
  },
  {
    name: 'Dawud',
    sources: [
      { label: 'Quran 34:10-11', url: 'https://quran.com/34/10-11' },
      { label: 'Quran 38:17-26', url: 'https://quran.com/38/17-26' },
      { label: 'Quran 4:163', url: 'https://quran.com/4/163' },
    ],
  },
  {
    name: 'Sulayman',
    sources: [
      { label: 'Quran 27:15-44', url: 'https://quran.com/27/15-44' },
      { label: 'Quran 34:12-14', url: 'https://quran.com/34/12-14' },
      { label: 'Quran 38:30-40', url: 'https://quran.com/38/30-40' },
    ],
  },
  {
    name: 'Zakariya',
    sources: [
      { label: 'Quran 19:2-11', url: 'https://quran.com/19/2-11' },
      { label: 'Quran 3:38-41', url: 'https://quran.com/3/38-41' },
      { label: 'Quran 21:89-90', url: 'https://quran.com/21/89-90' },
    ],
  },
  {
    name: 'Yahya',
    sources: [
      { label: 'Quran 19:12-15', url: 'https://quran.com/19/12-15' },
      { label: 'Quran 3:39', url: 'https://quran.com/3/39' },
    ],
  },
  {
    name: 'Isa',
    sources: [
      { label: 'Quran 3:45-55', url: 'https://quran.com/3/45-55' },
      { label: 'Quran 19:16-36', url: 'https://quran.com/19/16-36' },
      { label: 'Quran 5:110-117', url: 'https://quran.com/5/110-117' },
    ],
  },
  {
    name: 'Muhammad ﷺ',
    sources: [
      { label: 'Quran 21:107', url: 'https://quran.com/21/107' },
      { label: 'Quran 33:21', url: 'https://quran.com/33/21' },
      { label: 'Quran 68:4', url: 'https://quran.com/68/4' },
      { label: 'Quran 48:29', url: 'https://quran.com/48/29' },
      { label: 'Sahih al-Bukhari 631', url: 'https://sunnah.com/bukhari:631' },
    ],
  },
];

const drawingColours = ['#222222', '#ef4444', '#f97316', '#facc15', '#22c55e', '#06b6d4', '#6366f1', '#d946ef'];

const memoryDeck = ['quran', 'moon', 'star', 'home', 'heart', 'shield'];

const quizQuestions = [
  {
    question: 'What do we say before starting something good?',
    options: ['Bismillah', 'Good night', 'Hello'],
    answer: 'Bismillah',
  },
  {
    question: 'How many times do Muslims pray each day?',
    options: ['5', '2', '9'],
    answer: '5',
  },
  {
    question: 'Which book was sent to Prophet Muhammad ﷺ?',
    options: ['Quran', 'Map', 'Notebook'],
    answer: 'Quran',
  },
  {
    question: 'What do we say when we feel thankful?',
    options: ['Alhamdulillah', 'Oops', 'See you'],
    answer: 'Alhamdulillah',
  },
  {
    question: 'What is the greeting of peace?',
    options: ['Assalamu Alaikum', 'Good cake', 'Run fast'],
    answer: 'Assalamu Alaikum',
  },
  {
    question: 'What do we say when we see something amazing?',
    options: ['SubhanAllah', 'No thanks', 'Too late'],
    answer: 'SubhanAllah',
  },
  {
    question: 'What do Muslims face when praying?',
    options: ['Qiblah', 'Toy box', 'Television'],
    answer: 'Qiblah',
  },
  {
    question: 'What should we do before salah?',
    options: ['Make wudu', 'Eat sweets', 'Jump on the sofa'],
    answer: 'Make wudu',
  },
  {
    question: 'Which month do Muslims fast in?',
    options: ['Ramadan', 'January', 'Picnic day'],
    answer: 'Ramadan',
  },
  {
    question: 'What do we say when someone helps us?',
    options: ['JazakAllahu Khair', 'Go away', 'Nothing'],
    answer: 'JazakAllahu Khair',
  },
  {
    question: 'Where do Muslims often pray together?',
    options: ['Mosque', 'Swimming pool', 'Shop window'],
    answer: 'Mosque',
  },
  {
    question: 'Which action shows good manners?',
    options: ['Sharing kindly', 'Pushing', 'Shouting'],
    answer: 'Sharing kindly',
  },
  {
    question: 'What do we say when making a plan?',
    options: ['InshaAllah', 'Never ever', 'Banana'],
    answer: 'InshaAllah',
  },
  {
    question: 'What do Muslims recite in prayer?',
    options: ['Quran', 'Shopping list', 'Football scores'],
    answer: 'Quran',
  },
  {
    question: 'What is the first surah in the Quran?',
    options: ['Al Fatihah', 'Al Falaq', 'An Nas'],
    answer: 'Al Fatihah',
  },
  {
    question: 'What is a kind thing to do at home?',
    options: ['Help tidy up', 'Make a mess', 'Hide the shoes'],
    answer: 'Help tidy up',
  },
  {
    question: 'What should we do when we make a mistake?',
    options: ['Say sorry', 'Blame others', 'Laugh loudly'],
    answer: 'Say sorry',
  },
  {
    question: 'What do we say to remember Allah is the Greatest?',
    options: ['Allahu Akbar', 'Good morning', 'I am first'],
    answer: 'Allahu Akbar',
  },
  {
    question: 'Which hand should we try to use for eating?',
    options: ['Right hand', 'Left foot', 'Elbow'],
    answer: 'Right hand',
  },
  {
    question: 'What should we do when parents call us?',
    options: ['Answer kindly', 'Ignore them', 'Run away'],
    answer: 'Answer kindly',
  },
  {
    question: 'What do Muslims give to help people in need?',
    options: ['Charity', 'Loud noise', 'Broken toys only'],
    answer: 'Charity',
  },
  {
    question: 'What is a good deed?',
    options: ['Smiling kindly', 'Taking without asking', 'Being mean'],
    answer: 'Smiling kindly',
  },
];

const prophetQuizQuestions = [
  {
    question: 'Which Prophet built the ark by Allah’s command?',
    options: ['Nuh', 'Yusuf', 'Sulayman'],
    answer: 'Nuh',
  },
  {
    question: 'Which Prophet was known for forgiving after hard tests?',
    options: ['Yusuf', 'Hud', 'Salih'],
    answer: 'Yusuf',
  },
  {
    question: 'Which Prophet made dua in darkness inside the great fish?',
    options: ['Yunus', 'Musa', 'Zakariya'],
    answer: 'Yunus',
  },
  {
    question: 'Which Prophet was blessed with Zamzam in his family story?',
    options: ['Ismail', 'Dawud', 'Harun'],
    answer: 'Ismail',
  },
  {
    question: 'Which Prophet was a wise king who thanked Allah for his gifts?',
    options: ['Sulayman', 'Adam', 'Ayyub'],
    answer: 'Sulayman',
  },
  {
    question: 'Which Prophet teaches us patience during sickness and difficulty?',
    options: ['Ayyub', 'Isa', 'Shuayb'],
    answer: 'Ayyub',
  },
  {
    question: 'Who was the first Prophet and first human?',
    options: ['Adam', 'Nuh', 'Musa'],
    answer: 'Adam',
  },
  {
    question: 'Which Prophet spoke to Allah at Mount Sinai?',
    options: ['Musa', 'Yusuf', 'Hud'],
    answer: 'Musa',
  },
  {
    question: 'Which Prophet was known for a beautiful shirt in his story?',
    options: ['Yusuf', 'Ibrahim', 'Dawud'],
    answer: 'Yusuf',
  },
  {
    question: 'Which Prophet was saved from the fire by Allah?',
    options: ['Ibrahim', 'Zakariya', 'Idris'],
    answer: 'Ibrahim',
  },
  {
    question: 'Which Prophet had a brother named Harun?',
    options: ['Musa', 'Nuh', 'Salih'],
    answer: 'Musa',
  },
  {
    question: 'Which Prophet was born without a father by Allah’s command?',
    options: ['Isa', 'Ismail', 'Ishaq'],
    answer: 'Isa',
  },
  {
    question: 'Which Prophet was sent as the final Messenger?',
    options: ['Muhammad ﷺ', 'Yunus', 'Adam'],
    answer: 'Muhammad ﷺ',
  },
  {
    question: 'Which Prophet helped build the Kaaba with his father?',
    options: ['Ismail', 'Lut', 'Ayyub'],
    answer: 'Ismail',
  },
  {
    question: 'Which Prophet could understand the ants by Allah’s gift?',
    options: ['Sulayman', 'Yusuf', 'Isa'],
    answer: 'Sulayman',
  },
  {
    question: 'Which Prophet is remembered with the great fish?',
    options: ['Yunus', 'Nuh', 'Ibrahim'],
    answer: 'Yunus',
  },
  {
    question: 'Which Prophet was very patient when his people would not listen?',
    options: ['Nuh', 'Dawud', 'Harun'],
    answer: 'Nuh',
  },
  {
    question: 'Which Prophet had a dream about the sun, moon, and stars?',
    options: ['Yusuf', 'Musa', 'Isa'],
    answer: 'Yusuf',
  },
  {
    question: 'Which Prophet was given the Zabur?',
    options: ['Dawud', 'Adam', 'Hud'],
    answer: 'Dawud',
  },
  {
    question: 'Which Prophet was known for beautiful patience with illness?',
    options: ['Ayyub', 'Salih', 'Lut'],
    answer: 'Ayyub',
  },
  {
    question: 'Which Prophet’s mother placed him in a basket as a baby?',
    options: ['Musa', 'Yusuf', 'Muhammad ﷺ'],
    answer: 'Musa',
  },
  {
    question: 'Which Prophet taught people to worship Allah alone after idols?',
    options: ['Ibrahim', 'Yunus', 'Zakariya'],
    answer: 'Ibrahim',
  },
];

const matchingPairs = [
  { left: 'Bismillah', right: 'Before we begin' },
  { left: 'Alhamdulillah', right: 'Thank Allah' },
  { left: 'Assalamu Alaikum', right: 'Peace greeting' },
  { left: 'JazakAllahu Khair', right: 'Thank you dua' },
];

const bismillahPuzzle = [
  { id: 'bism', text: 'بِسْمِ', hint: 'Bismi' },
  { id: 'allah', text: 'اللهِ', hint: 'Allah' },
  { id: 'rahman', text: 'الرَّحْمٰنِ', hint: 'Ar-Rahman' },
  { id: 'raheem', text: 'الرَّحِيمِ', hint: 'Ar-Raheem' },
];

const snakeQuestions = [
  {
    question: 'What do we say before starting something good?',
    options: ['Bismillah', 'Goodbye', 'Maybe later'],
    answer: 'Bismillah',
  },
  {
    question: 'Which action is a good deed?',
    options: ['Helping tidy up', 'Grabbing toys', 'Shouting loudly'],
    answer: 'Helping tidy up',
  },
  {
    question: 'How many daily prayers do Muslims pray?',
    options: ['5', '1', '9'],
    answer: '5',
  },
  {
    question: 'What do we say when someone helps us?',
    options: ['JazakAllahu Khair', 'Move away', 'I forgot'],
    answer: 'JazakAllahu Khair',
  },
  {
    question: 'Which book do Muslims recite?',
    options: ['Quran', 'Comic book', 'Menu'],
    answer: 'Quran',
  },
  {
    question: 'What should we do in salah?',
    options: ['Pray calmly', 'Run around', 'Talk loudly'],
    answer: 'Pray calmly',
  },
  {
    question: 'Which choice is kind?',
    options: ['Let someone have a turn', 'Snatch the toy', 'Laugh at someone'],
    answer: 'Let someone have a turn',
  },
  {
    question: 'What do we say after sneezing or feeling thankful?',
    options: ['Alhamdulillah', 'No way', 'I win'],
    answer: 'Alhamdulillah',
  },
  {
    question: 'What helps us get ready for prayer?',
    options: ['Wudu', 'Running fast', 'Eating crisps'],
    answer: 'Wudu',
  },
  {
    question: 'What do we say before eating?',
    options: ['Bismillah', 'Finished', 'Again again'],
    answer: 'Bismillah',
  },
  {
    question: 'Which is good adab?',
    options: ['Speaking gently', 'Interrupting', 'Throwing things'],
    answer: 'Speaking gently',
  },
  {
    question: 'What should we do with the Quran?',
    options: ['Respect it', 'Leave it on the floor', 'Throw it'],
    answer: 'Respect it',
  },
  {
    question: 'What is a good deed outside?',
    options: ['Pick up litter safely', 'Push someone', 'Break flowers'],
    answer: 'Pick up litter safely',
  },
  {
    question: 'What should we say when we greet Muslims?',
    options: ['Salam', 'Go away', 'Hurry up'],
    answer: 'Salam',
  },
  {
    question: 'Which action earns love?',
    options: ['Being truthful', 'Lying', 'Hiding mistakes'],
    answer: 'Being truthful',
  },
  {
    question: 'What do we do in sujood?',
    options: ['Put forehead down', 'Clap loudly', 'Spin around'],
    answer: 'Put forehead down',
  },
  {
    question: 'Which word means Allah is the Greatest?',
    options: ['Allahu Akbar', 'Alif', 'Mosque'],
    answer: 'Allahu Akbar',
  },
  {
    question: 'What should we do when someone is sad?',
    options: ['Be gentle', 'Tease them', 'Ignore them'],
    answer: 'Be gentle',
  },
  {
    question: 'Which one is sadaqah?',
    options: ['Helping someone', 'Taking their snack', 'Making fun'],
    answer: 'Helping someone',
  },
  {
    question: 'What should we do before sleeping?',
    options: ['Remember Allah', 'Shout loudly', 'Make a mess'],
    answer: 'Remember Allah',
  },
];

const deedSortItems = [
  { id: 'share', text: 'Share your toy', kind: 'good', icon: 'heart' },
  { id: 'salam', text: 'Say Salam first', kind: 'good', icon: 'check' },
  { id: 'shout', text: 'Shout at someone', kind: 'try', icon: 'shield' },
  { id: 'help', text: 'Help tidy up', kind: 'good', icon: 'star' },
  { id: 'grab', text: 'Grab without asking', kind: 'try', icon: 'shield' },
  { id: 'truth', text: 'Tell the truth', kind: 'good', icon: 'heart' },
];

const salahSteps = [
  'Make wudu',
  'Face the Qiblah',
  'Make your intention',
  'Say Allahu Akbar',
  'Pray calmly',
  'Finish with salam',
];

const dailySalahs = [
  {
    id: 'fajr',
    name: 'Fajr',
    time: 'Before sunrise',
    rakahs: '2 fard rakahs',
    note: 'A peaceful prayer to begin the day with Allah.',
    icon: 'star',
  },
  {
    id: 'dhuhr',
    name: 'Dhuhr',
    time: 'Middle of the day',
    rakahs: '4 fard rakahs',
    note: 'A prayer break when the day is bright and busy.',
    icon: 'star',
  },
  {
    id: 'asr',
    name: 'Asr',
    time: 'Afternoon',
    rakahs: '4 fard rakahs',
    note: 'A reminder to slow down and remember Allah.',
    icon: 'star',
  },
  {
    id: 'maghrib',
    name: 'Maghrib',
    time: 'Just after sunset',
    rakahs: '3 fard rakahs',
    note: 'A prayer when the sky changes and the evening begins.',
    icon: 'moon',
  },
  {
    id: 'isha',
    name: 'Isha',
    time: 'Night',
    rakahs: '4 fard rakahs',
    note: 'A calm prayer before rest and sleep.',
    icon: 'moon',
  },
];

const wuduSteps = [
  { id: 'intention', title: 'Intention and Bismillah', detail: 'Get ready in your heart and begin with Bismillah.', icon: 'heart' },
  { id: 'hands', title: 'Wash Hands', detail: 'Wash both hands carefully.', icon: 'water' },
  { id: 'mouth', title: 'Rinse Mouth', detail: 'Rinse gently with clean water.', icon: 'water' },
  { id: 'nose', title: 'Clean Nose', detail: 'Clean your nose gently.', icon: 'water' },
  { id: 'face', title: 'Wash Face', detail: 'Wash your whole face.', icon: 'water' },
  { id: 'arms', title: 'Wash Arms', detail: 'Wash arms up to the elbows.', icon: 'water' },
  { id: 'head', title: 'Wipe Head', detail: 'Wipe over the head with wet hands.', icon: 'water' },
  { id: 'ears', title: 'Wipe Ears', detail: 'Wipe the ears gently.', icon: 'water' },
  { id: 'feet', title: 'Wash Feet', detail: 'Wash both feet carefully.', icon: 'water' },
];

const prayerGuideSteps = [
  {
    id: 'wudu',
    title: 'Make Wudu',
    arabic: 'الوضوء',
    visual: 'water',
    pose: 'wudu',
    guidance: 'Wash carefully and get ready to stand before Allah.',
    childTip: 'Ask a grown-up to help you learn each part.',
  },
  {
    id: 'qiblah',
    title: 'Face the Qiblah',
    arabic: 'القبلة',
    visual: 'home',
    pose: 'qiblah',
    guidance: 'Stand on a clean prayer mat and face the Kaaba.',
    childTip: 'Keep your feet still and your heart calm.',
  },
  {
    id: 'niyyah',
    title: 'Make Your Intention',
    arabic: 'النية',
    visual: 'heart',
    pose: 'intention',
    guidance: 'Know in your heart which prayer you are praying.',
    childTip: 'Allah knows what is inside your heart.',
  },
  {
    id: 'takbir',
    title: 'Say Allahu Akbar',
    arabic: 'الله أكبر',
    visual: 'star',
    pose: 'takbir',
    guidance: 'Raise your hands and begin the prayer.',
    childTip: 'Now focus on Allah and pray gently.',
  },
  {
    id: 'qiyam',
    title: 'Stand and Recite',
    arabic: 'القيام',
    visual: 'book',
    pose: 'qiyam',
    guidance: 'Stand nicely and recite Al Fatihah and Quran.',
    childTip: 'Start with the short surahs you are learning.',
  },
  {
    id: 'ruku',
    title: 'Bow in Ruku',
    arabic: 'الركوع',
    visual: 'moon',
    pose: 'ruku',
    guidance: 'Bow calmly and remember Allah is Most Great.',
    childTip: 'Keep your back steady like a little table.',
  },
  {
    id: 'sujood',
    title: 'Make Sujood',
    arabic: 'السجود',
    visual: 'star',
    pose: 'sujood',
    guidance: 'Put your forehead down and make dua to Allah.',
    childTip: 'This is a very special moment to ask Allah for good.',
  },
  {
    id: 'sitting',
    title: 'Sit Calmly',
    arabic: 'الجلوس',
    visual: 'star',
    pose: 'sitting',
    guidance: 'Sit quietly between sujood and near the end of prayer.',
    childTip: 'Keep your hands gentle and your voice soft.',
  },
  {
    id: 'salam',
    title: 'Finish with Salam',
    arabic: 'السلام',
    visual: 'heart',
    pose: 'salam',
    guidance: 'Turn gently and say salam to finish your prayer.',
    childTip: 'Smile and thank Allah for helping you pray.',
  },
];

const audioClipCache = new Map();
const audioBlobUrlCache = new Map();
const OLIVER_AUDIO_CACHE = 'little-muslim-explorer-oliver-audio-v1';
const OLIVER_TYPECAST_ENDPOINT = 'https://us-central1-rangi-bda5e.cloudfunctions.net/synthesizeTypecastSpeech';
let activeAudioClip = null;

function stableAudioKey(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function bytesFromBase64(base64) {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

async function blobUrlFromCache(cacheKey) {
  if (!('caches' in window)) return null;
  const cache = await window.caches.open(OLIVER_AUDIO_CACHE);
  const response = await cache.match(`/__oliver_audio__/${cacheKey}.wav`);
  if (!response) return null;
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

async function storeBlobUrlInCache(cacheKey, bytes) {
  const blob = new Blob([bytes], { type: 'audio/wav' });
  if ('caches' in window) {
    const cache = await window.caches.open(OLIVER_AUDIO_CACHE);
    await cache.put(
      `/__oliver_audio__/${cacheKey}.wav`,
      new Response(blob, { headers: { 'Content-Type': 'audio/wav', 'Cache-Control': 'public, max-age=31536000, immutable' } })
    );
  }
  return URL.createObjectURL(blob);
}

async function getCachedTypecastAudioUrl(text, cacheKey = stableAudioKey(text), avatarId = 'story') {
  const key = `${avatarId}-${cacheKey}`;
  if (audioBlobUrlCache.has(key)) return audioBlobUrlCache.get(key);

  const cachedUrl = await blobUrlFromCache(key);
  if (cachedUrl) {
    audioBlobUrlCache.set(key, cachedUrl);
    return cachedUrl;
  }

  const response = await fetch(OLIVER_TYPECAST_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, avatarId }),
  });

  if (!response.ok) {
    throw new Error(`Oliver Typecast audio failed: ${response.status}`);
  }

  const data = await response.json();
  const bytes = bytesFromBase64(data.audioContent);
  const url = await storeBlobUrlInCache(key, bytes);
  audioBlobUrlCache.set(key, url);
  return url;
}

async function getCachedStaticAudioUrl(src) {
  if (!('caches' in window)) return src;
  const key = `static-${stableAudioKey(src)}`;
  if (audioBlobUrlCache.has(key)) return audioBlobUrlCache.get(key);

  const cachedUrl = await blobUrlFromCache(key);
  if (cachedUrl) {
    audioBlobUrlCache.set(key, cachedUrl);
    return cachedUrl;
  }

  const response = await fetch(src);
  if (!response.ok) return src;
  const bytes = new Uint8Array(await response.arrayBuffer());
  const url = await storeBlobUrlInCache(key, bytes);
  audioBlobUrlCache.set(key, url);
  return url;
}

function speakFallback(text, reward) {
  if (!text || !('speechSynthesis' in window)) return false;

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = /[\u0600-\u06FF]/.test(text) ? 'ar' : 'en-GB';
  utterance.rate = 0.82;
  utterance.pitch = 1.08;
  window.speechSynthesis.speak(utterance);
  reward?.('Voice is playing');
  return true;
}

function pause(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function playAudioUrl(src, reward, message, fallbackText = '', options = {}) {
  stopSpeech();
  if (activeAudioClip) {
    activeAudioClip.pause();
    activeAudioClip.currentTime = 0;
  }

  const audio = audioClipCache.get(src) || new Audio(src);
  audioClipCache.set(src, audio);
  activeAudioClip = audio;
  audio.preload = 'auto';
  audio.volume = 1;
  audio.currentTime = 0;

  return new Promise((resolve) => {
    let settled = false;
    const settle = (result) => {
      if (settled) return;
      settled = true;
      audio.onended = null;
      audio.onerror = null;
      resolve(result);
    };

    audio.onended = () => settle(audio);
    audio.onerror = () => settle(null);

    audio.play()
      .then(() => {
        if (message) reward(message);
      })
      .catch((error) => {
        console.warn('[OliverVoice] Audio unavailable:', error);
        if (options.allowFallback !== false && !speakFallback(fallbackText, reward)) {
          reward('Oliver voice is loading. Try again in a moment.');
        }
        settle(null);
      });
  });
}

async function playOliverAudio(src, reward, message, fallbackText = '') {
  try {
    const cachedSrc = await getCachedStaticAudioUrl(src);
    return playAudioUrl(cachedSrc, reward, message, fallbackText);
  } catch (error) {
    console.warn('[OliverVoice] Cache unavailable:', error);
    return playAudioUrl(src, reward, message, fallbackText);
  }
}

async function playTypecastOliverText(text, reward, message, cacheKey) {
  try {
    const src = await getCachedTypecastAudioUrl(text, cacheKey);
    return playAudioUrl(src, reward, message, '', { allowFallback: false });
  } catch (error) {
    console.warn('[OliverVoice] Typecast audio unavailable:', error);
    reward?.('Oliver voice is loading. Try again in a moment.');
    return null;
  }
}

function stopSpeech() {
  if (activeAudioClip) {
    activeAudioClip.pause();
    activeAudioClip.currentTime = 0;
  }

  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}

function getYouTubeEmbedUrl(url) {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace('www.', '');
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = parsed.pathname.slice(1);
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      videoId = parsed.searchParams.get('v') || '';
      if (!videoId && parsed.pathname.startsWith('/embed/')) {
        videoId = parsed.pathname.replace('/embed/', '');
      }
    }

    if (!videoId) {
      return '';
    }

    return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`;
  } catch (error) {
    return '';
  }
}

function useOnlineStatus() {
  const getOnlineStatus = () => (
    typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean'
      ? true
      : navigator.onLine
  );
  const [isOnline, setIsOnline] = useState(getOnlineStatus);

  useEffect(() => {
    const updateStatus = () => setIsOnline(getOnlineStatus());

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    updateStatus();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  return isOnline;
}

function isIOSNativeRuntime() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const capacitor = window.Capacitor;
  if (capacitor?.isNativePlatform?.() && capacitor?.getPlatform?.() === 'ios') {
    return true;
  }

  const protocol = window.location?.protocol || '';
  const userAgent = navigator.userAgent || '';
  const isNativeScheme = protocol === 'capacitor:' || protocol === 'ionic:';
  const isAppleMobile = /iPad|iPhone|iPod/.test(userAgent);

  return isNativeScheme && isAppleMobile;
}

function isNativeMobileRuntime() {
  if (typeof window === 'undefined') return false;

  const capacitor = window.Capacitor;
  if (capacitor?.isNativePlatform?.()) {
    const platform = capacitor.getPlatform?.();
    return platform === 'ios' || platform === 'android';
  }

  const protocol = window.location?.protocol || '';
  return protocol === 'capacitor:' || protocol === 'ionic:';
}

function AgeGate({ onChoose }) {
  return (
    <div className="explorer-app age-gate-app">
      <div className="sparkle-field" aria-hidden="true">
        {sparkleField.map((sparkle, index) => (
          <span
            key={index}
            className={sparkle.tone ? `tone-${sparkle.tone}` : ''}
            style={{ top: sparkle.top, left: sparkle.left, width: sparkle.size, height: sparkle.size, animationDelay: sparkle.delay }}
          />
        ))}
      </div>
      <div className="age-gate">
        <div className="age-gate-animals" aria-hidden="true">
          {['cat', 'lamb', 'turtle', 'duck', 'bird', 'lion'].map((id) => (
            <span key={id} className={`age-animal age-animal-${id}`}>
              <img src={animalImages[id]} alt="" />
            </span>
          ))}
        </div>
        <div className="age-gate-card">
          <ExplorerLogo />
          <h1>How old is your little explorer?</h1>
          <p>This helps us show activities that fit them best.</p>
          <div className="age-gate-options">
            <button type="button" className="age-gate-option theme-mint" onClick={() => onChoose('under6')}>
              <PawPrint size={40} strokeWidth={2.5} />
              <strong>Under 6</strong>
              <small>Simple tap-and-play games</small>
            </button>
            <button type="button" className="age-gate-option theme-ocean" onClick={() => onChoose('over6')}>
              <BookOpen size={40} strokeWidth={2.5} />
              <strong>6 and up</strong>
              <small>Reading, quizzes and more</small>
            </button>
          </div>
          <p className="age-gate-note">A grown-up can change this anytime in For Parents.</p>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [ageGroup, setAgeGroup] = useState(() => sessionStorage.getItem(AGE_GROUP_KEY) || '');
  const [activeSection, setActiveSection] = useState(() => (
    sessionStorage.getItem(AGE_GROUP_KEY) === 'under6' ? 'animals' : 'home'
  ));
  const [toast, setToast] = useState('');
  const isOnline = useOnlineStatus();
  const isNativeMobile = isNativeMobileRuntime();
  const { progress, completeItem, toggleItem } = useExplorerProgress();

  useEffect(() => {
    const meta = sections.find((section) => section.id === activeSection);
    trackScreenView(activeSection, meta ? meta.label : activeSection);
  }, [activeSection]);

  const reward = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 1800);
  };

  const handleComplete = (type, id, _unused, message) => {
    completeItem(type, id);
    trackEvent('activity_completed', { activity_type: type, activity_id: id });
    reward(message || 'Completed!');
  };

  const navigateToSection = (sectionId, scrollTarget = 'section') => {
    setActiveSection(sectionId);
    window.setTimeout(() => {
      const target = scrollTarget === 'top'
        ? document.querySelector('.explorer-app')
        : document.getElementById('active-section-content');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const chooseAgeGroup = (group) => {
    localStorage.removeItem(AGE_GROUP_KEY);
    sessionStorage.setItem(AGE_GROUP_KEY, group);
    setAgeGroup(group);
    setActiveSection(group === 'under6' ? 'animals' : 'home');
    trackEvent('age_group_selected', { age_group: group });
  };

  const resetAgeGroup = () => {
    localStorage.removeItem(AGE_GROUP_KEY);
    sessionStorage.removeItem(AGE_GROUP_KEY);
    setAgeGroup('');
  };

  if (!ageGroup) {
    return <AgeGate onChoose={chooseAgeGroup} />;
  }

  const visibleSections = ageGroup === 'under6'
    ? sections.filter((section) => under6SectionIds.includes(section.id))
    : sections;
  const activeMeta = sections.find((section) => section.id === activeSection) || sections[0];
  const ActiveIcon = activeMeta.Icon;

  return (
    <div className="explorer-app">
      <div className="sky-shapes" aria-hidden="true">
        {skyMascots.map((id) => {
          const animal = getAnimal(id);
          return (
            <span key={id} className={`sky-shape-${id}`} style={{ '--shape-start': animal.colors[0], '--shape-end': animal.colors[1] }}>
              <img src={animalImages[id]} alt="" />
            </span>
          );
        })}
      </div>

      <div className="sparkle-field" aria-hidden="true">
        {sparkleField.map((sparkle, index) => (
          <span
            key={index}
            className={sparkle.tone ? `tone-${sparkle.tone}` : ''}
            style={{ top: sparkle.top, left: sparkle.left, width: sparkle.size, height: sparkle.size, animationDelay: sparkle.delay }}
          />
        ))}
      </div>

      <header className="app-header">
        <button className="brand-lockup" onClick={() => navigateToSection('home', 'top')} aria-label="Go home">
          <ExplorerLogo />
          <span>
            <strong>Little Muslim Explorer</strong>
            <small>Play, learn, create</small>
          </span>
        </button>

        <div className="top-stats" aria-label="Parent links">
          <button type="button" className="feedback-link age-switch-button" onClick={resetAgeGroup}>
            <PawPrint size={18} />
            {ageGroup === 'under6' ? 'Under 6 Mode' : '6+ Mode'}
          </button>
          <a className="feedback-link" href={feedbackFormUrl} target="_blank" rel="noreferrer">
            <Heart size={18} />
            Parent Feedback
          </a>
          <a className="feedback-link instagram-link" href={instagramUrl} target="_blank" rel="noreferrer">
            <Instagram size={18} />
            Instagram
          </a>
        </div>
      </header>

      <main>
        {activeSection === 'home' && (
          <section className={`hero-panel theme-${activeMeta.color}`}>
            <div>
              <p className="eyebrow hero-eyebrow"><ActiveIcon size={18} /> {activeMeta.label}</p>
              <h1>
                Assalamu Alaikum,
                <span className="hero-title-accent">little explorer!</span>
              </h1>
              <p className="hero-copy">
                Islamic learning, stories, quizzes and fun activities for Muslim children.
              </p>
              <div className="hero-actions">
                <button className="primary-action" onClick={() => navigateToSection('quran')}>
                  <Play size={22} />
                  Continue Learning
                </button>
              </div>
            </div>
          </section>
        )}

        <nav className="section-grid" aria-label="Main learning areas">
          {visibleSections.map(({ id, label, color, Icon }) => (
            <button
              key={id}
              className={`section-card theme-${color} ${activeSection === id ? 'active' : ''}`}
              onClick={() => navigateToSection(id)}
            >
              <span className="section-icon-badge"><Icon size={34} strokeWidth={2.5} /></span>
              <span>{label}</span>
              <Icon size={22} />
            </button>
          ))}
        </nav>

        <div id="active-section-content" className="active-section-content">
          {activeSection === 'home' && <HomeScreen navigateToSection={navigateToSection} isNativeMobile={isNativeMobile} />}
          {activeSection === 'parents' && <ForParentsSection ageGroup={ageGroup} onChangeAgeGroup={resetAgeGroup} />}
          {activeSection === 'play' && <PlaySection onComplete={handleComplete} progress={progress} />}
          {activeSection === 'animals' && <AnimalAdventureSection onComplete={handleComplete} progress={progress} reward={reward} />}
          {activeSection === 'quran' && <QuranSection onComplete={handleComplete} progress={progress} />}
          {activeSection === 'arabic' && <ArabicSection onComplete={handleComplete} progress={progress} />}
          {activeSection === 'words' && <WordsSection onComplete={handleComplete} progress={progress} />}
          {activeSection === 'prayer' && <PrayerSection />}
          {activeSection === 'prophets' && <ProphetsSection onComplete={handleComplete} progress={progress} />}
          {activeSection === 'create' && <CreateSection reward={reward} />}
          {activeSection === 'nasheeds' && <NasheedsSection isOnline={isOnline} progress={progress} toggleItem={toggleItem} onComplete={handleComplete} reward={reward} />}
          {activeSection === 'videos' && <VideosSection isOnline={isOnline} />}
          {activeSection === 'privacy' && <PrivacyPolicySection />}
          {activeSection === 'terms' && <TermsSection />}
        </div>
      </main>

      <AppFooter navigateToSection={navigateToSection} />

      {toast && (
        <div className="toast" role="status">
          <Check size={20} />
          {toast}
        </div>
      )}
    </div>
  );
}

function ExplorerLogo() {
  return (
    <span className="explorer-logo" aria-hidden="true">
      <img src="/images/little-muslim-explorer-logo.png" alt="" />
    </span>
  );
}

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function getDailyReminder() {
  const today = new Date();
  const start = new Date(today.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((today - start) / 86400000);
  return mobileLearningReminders[dayOfYear % mobileLearningReminders.length];
}

function MobileLearningReminder({ navigateToSection }) {
  const reminder = getDailyReminder();
  const todayKey = getTodayKey();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('little_muslim_mobile_reminder_dismissed') === todayKey);

  if (dismissed) return null;

  const dismissReminder = () => {
    localStorage.setItem('little_muslim_mobile_reminder_dismissed', todayKey);
    setDismissed(true);
  };

  return (
    <article className="mobile-learning-reminder" role="status">
      <span className="mobile-reminder-icon">
        <BellRing size={28} />
      </span>
      <div>
        <p className="kicker">{reminder.title}</p>
        <h3>{reminder.message}</h3>
      </div>
      <div className="mobile-reminder-actions">
        <button onClick={() => navigateToSection(reminder.sectionId)}>
          <Play size={18} />
          {reminder.actionLabel}
        </button>
        <button type="button" onClick={dismissReminder}>Later</button>
      </div>
    </article>
  );
}

function HomeScreen({ navigateToSection, isNativeMobile }) {
  return (
    <section className="content-grid home-grid">
      {isNativeMobile && <MobileLearningReminder navigateToSection={navigateToSection} />}
      <div className="home-avatar-banner">
        <KidsAvatar />
        <div>
          <p className="kicker">Welcome Explorers</p>
          <h2>Assalamu Alaikum, little learners!</h2>
          <p>Choose an adventure, play gently, and learn something beautiful today.</p>
        </div>
      </div>
      <InfoTile title="Toddler Section" value="Tap animals, count, match, and sing along" icon={<PawPrint />} color="garden" />
      <InfoTile title="Quran" value="Listen and memorise short surahs" icon={<BookOpen />} color="ocean" />
      <InfoTile title="Arabic" value="Hear, match, and trace letters" icon={<Baby />} color="berry" />
      <InfoTile title="Prayer" value="See simple salah movement pictures" icon={<ShieldCheck />} color="leaf" />
      <TrustSection />
      <div className="wide-panel theme-paint">
        <div>
          <h2>Ready for today’s adventure?</h2>
          <p>Pick a colourful card, learn something beautiful, and celebrate each completed activity.</p>
        </div>
        <button className="primary-action" onClick={() => navigateToSection('arabic')}>
          <Play size={22} />
          Continue Learning
        </button>
      </div>
    </section>
  );
}

function TrustSection() {
  const trustItems = [
    { title: 'No ads', description: 'No advertising or noisy distractions inside the learning experience.', icon: 'shield' },
    { title: 'Child-safe experience', description: 'Simple sections, parent-approved content, and no chat or public profiles.', icon: 'shield' },
    { title: 'Made for Muslim families', description: 'Built around Islamic learning, good manners, Quran, stories, and daily practice.', icon: 'home' },
    { title: 'Suitable for young children', description: 'Bright buttons, gentle wording, clear sounds, and activities children can explore.', icon: 'star' },
    { title: 'Islamic stories, quizzes and activities', description: 'Prophet stories, Arabic practice, quizzes, drawing, nasheeds, and safe videos.', icon: 'book' },
  ];

  return (
    <section className="trust-section">
      <div className="section-mini-heading">
        <p className="kicker">Parent Trust</p>
        <h2>A safer space for curious Muslim children</h2>
      </div>
      <div className="trust-grid">
        {trustItems.map((item) => (
          <article key={item.title} className="trust-card">
            <DecorIcon type={item.icon} size={30} />
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function ForParentsSection({ ageGroup, onChangeAgeGroup }) {
  return (
    <Section
      title="For Parents"
      subtitle="A warm, focused Islamic learning space for children."
    >
      <div className="parent-page-grid">
        <article className="parent-page-card theme-leaf">
          <p className="kicker">Why it helps</p>
          <h3>Less random browsing, more meaningful learning</h3>
          <p>
            Little Muslim Explorer gives children a safer, more focused Islamic learning experience compared with random browsing or open-ended video watching. Children can explore Quran, Arabic, Prophet stories, prayer, quizzes, drawing, nasheeds, and parent-approved videos in one gentle place.
          </p>
        </article>
        <article className="parent-page-card theme-ocean">
          <p className="kicker">Built for families</p>
          <h3>Simple, colourful and easy to supervise</h3>
          <p>
            The app is designed for young Muslim children with large buttons, friendly visuals, clear sections, and minimal reading. Parents can sit nearby, guide activities, and use the feedback form to tell us what would make it better.
          </p>
        </article>
        <article className="parent-page-card theme-berry">
          <p className="kicker">Age group</p>
          <h3>{ageGroup === 'under6' ? 'Showing: Under 6' : 'Showing: 6 and up'}</h3>
          <p>
            {ageGroup === 'under6'
              ? 'Your explorer lands straight in the Toddler Section with a simpler menu of tap-and-play activities.'
              : 'Your explorer sees the full app, including reading, quizzes, and memorisation activities.'}
          </p>
          <button className="primary-action" onClick={onChangeAgeGroup}>
            <RefreshCw size={20} />
            Change age group
          </button>
        </article>
        <article className="parent-page-card parent-support-card">
          <p className="kicker">Support</p>
          <h3>Need help or have feedback?</h3>
          <p>Need help or have feedback? use the form.</p>
          <a className="primary-action" href={feedbackFormUrl} target="_blank" rel="noreferrer">
            <Heart size={20} />
            Open feedback form
          </a>
        </article>
      </div>
    </Section>
  );
}

function PrivacyPolicySection() {
  return (
    <Section title="Privacy Policy" subtitle="Placeholder policy for Little Muslim Explorer.">
      <article className="legal-page">
        <h3>Privacy-first placeholder</h3>
        <p>
          Little Muslim Explorer is designed to be child-safe and parent-friendly. The current MVP stores child progress locally on the device using browser storage. It does not include chat, public profiles, or advertising.
        </p>
        <p>
          A full Privacy Policy will be added before any wider public launch, app store release, parent accounts, payments, or cloud progress syncing.
        </p>
      </article>
    </Section>
  );
}

function TermsSection() {
  return (
    <Section title="Terms" subtitle="Placeholder terms for Little Muslim Explorer.">
      <article className="legal-page">
        <h3>Terms placeholder</h3>
        <p>
          Little Muslim Explorer is currently provided as an early learning MVP for parent testing and feedback. Content is intended for family learning and should be used with parent guidance.
        </p>
        <p>
          Full terms will be added before paid features, accounts, school use, or app store release.
        </p>
      </article>
    </Section>
  );
}

function AppFooter({ navigateToSection }) {
  return (
    <footer className="app-footer">
      <div>
        <strong>Little Muslim Explorer</strong>
        <p>Islamic learning, stories, quizzes and fun activities for Muslim children.</p>
      </div>
      <div className="footer-support">
        <p>Need help or have feedback? use the form</p>
        <div className="footer-social-links">
          <a href={feedbackFormUrl} target="_blank" rel="noreferrer">Feedback form</a>
          <a href={instagramUrl} target="_blank" rel="noreferrer">
            <Instagram size={17} />
            Instagram
          </a>
        </div>
      </div>
      <nav className="footer-links" aria-label="Footer links">
        <button onClick={() => navigateToSection('parents')}>For Parents</button>
        <button onClick={() => navigateToSection('privacy')}>Privacy Policy</button>
        <button onClick={() => navigateToSection('terms')}>Terms</button>
      </nav>
    </footer>
  );
}

function InfoTile({ title, value, icon, color }) {
  return (
    <article className={`info-tile theme-${color}`}>
      <span>{icon}</span>
      <h3>{title}</h3>
      <p>{value}</p>
    </article>
  );
}

function PlaySection({ onComplete, progress }) {
  const [activeGameId, setActiveGameId] = useState(games[0].id);
  const stageRef = useRef(null);
  const activeGame = games.find((game) => game.id === activeGameId) || games[0];
  const completed = progress.completedGames.includes(activeGame.id);

  const finishGame = () => {
    onComplete('completedGames', activeGame.id, null, `${activeGame.title} complete!`);
  };

  const selectGame = (id) => {
    setActiveGameId(id);
    stageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Section title="Play and Learn" subtitle="Mini games are ready for parent testing and save progress locally.">
      <div className="game-layout">
        <div className="game-menu" aria-label="Choose a game">
          {games.map((game) => {
            const gameCompleted = progress.completedGames.includes(game.id);
            return (
              <button
                key={game.id}
                className={`game-choice theme-${game.color} ${activeGameId === game.id ? 'active' : ''} ${gameCompleted ? 'completed' : ''}`}
                onClick={() => selectGame(game.id)}
              >
                <GameIcon gameId={game.id} />
                <strong>{game.title}</strong>
                <small>{gameCompleted ? <><Star size={12} /> Completed</> : game.category}</small>
              </button>
            );
          })}
        </div>

        <article ref={stageRef} className={`game-stage ${completed ? 'completed' : ''}`}>
          <div className="game-stage-header">
            <div>
              <p className={`kicker kicker-chip theme-${activeGame.color}`}>{activeGame.category}</p>
              <h3 className="game-title"><GameIcon gameId={activeGame.id} compact /> {activeGame.title}</h3>
              <p>{activeGame.description}</p>
            </div>
            {completed && <span className="badge badge-win"><Award size={16} /> Completed</span>}
          </div>
          {activeGame.id === 'snakes-ladders' && <SnakesLaddersGame onWin={finishGame} />}
          {activeGame.id === 'memory-match' && <MemoryMatchGame onWin={finishGame} />}
          {activeGame.id === 'islamic-quiz' && <IslamicQuizGame onWin={finishGame} />}
          {activeGame.id === 'prophet-quiz' && <ProphetQuizGame onWin={finishGame} />}
          {activeGame.id === 'matching-game' && <MatchingGame onWin={finishGame} />}
          {activeGame.id === 'letter-match' && <LetterMatchGame onWin={finishGame} />}
          {activeGame.id === 'alphabet-tracing' && <AlphabetTracingGame onWin={finishGame} />}
          {activeGame.id === 'good-deed-sort' && <GoodDeedSortGame onWin={finishGame} />}
          {activeGame.id === 'salah-steps' && <SalahStepsGame onWin={finishGame} />}
          {activeGame.id === 'puzzle-game' && <PuzzleGame onWin={finishGame} />}
          {activeGame.id === 'tic-tac-toe' && <TicTacToeGame onWin={finishGame} />}
        </article>
      </div>
    </Section>
  );
}

const animalGames = [
  { id: 'match', title: 'Animal Match', instruction: 'Pick the animal.', color: 'sunset' },
  { id: 'feed', title: 'Feed Animals', instruction: 'Give each animal its food.', color: 'mint' },
  { id: 'count', title: 'Count Animals', instruction: 'Tap and count.', color: 'ocean' },
  { id: 'pairs', title: 'Animal Pairs', instruction: 'Match the pairs.', color: 'plum' },
  { id: 'family-song', title: 'Family Song', instruction: 'Tap each duck to sing along together!', color: 'teal' },
];

const numberWords = ['One', 'Two', 'Three', 'Four', 'Five'];
const animalPraise = ['Well done!', 'MashaAllah!', 'Great job!'];
const animalImages = {
  bird: animalBird,
  camel: animalCamel,
  cat: animalCat,
  cow: animalCow,
  duck: animalDuck,
  lamb: animalLamb,
  lion: animalLion,
  monkey: animalMonkey,
  rabbit: animalRabbit,
  turtle: animalTurtle,
};

const skyMascots = ['cat', 'lion', 'turtle', 'lamb', 'duck', 'bird', 'monkey'];

const sparkleField = [
  { top: '4%', left: '6%', size: 10, delay: '0s' },
  { top: '9%', left: '46%', size: 7, delay: '1.1s' },
  { top: '6%', left: '89%', size: 11, delay: '0.5s' },
  { top: '16%', left: '18%', size: 8, delay: '1.8s' },
  { top: '14%', left: '68%', size: 9, delay: '0.3s' },
  { top: '24%', left: '3%', size: 10, delay: '2.2s', tone: 'pink' },
  { top: '27%', left: '38%', size: 7, delay: '0.9s' },
  { top: '22%', left: '96%', size: 9, delay: '1.5s' },
  { top: '35%', left: '58%', size: 8, delay: '0.2s', tone: 'teal' },
  { top: '38%', left: '8%', size: 11, delay: '1.3s' },
  { top: '33%', left: '80%', size: 7, delay: '2.6s' },
  { top: '47%', left: '28%', size: 9, delay: '0.7s' },
  { top: '50%', left: '95%', size: 10, delay: '1.9s' },
  { top: '46%', left: '2%', size: 8, delay: '0.4s' },
  { top: '58%', left: '65%', size: 7, delay: '2.1s', tone: 'pink' },
  { top: '61%', left: '12%', size: 10, delay: '1s' },
  { top: '55%', left: '44%', size: 8, delay: '1.6s' },
  { top: '68%', left: '90%', size: 9, delay: '0.6s' },
  { top: '71%', left: '20%', size: 11, delay: '2.4s' },
  { top: '65%', left: '55%', size: 7, delay: '1.4s', tone: 'teal' },
  { top: '80%', left: '5%', size: 10, delay: '0.8s' },
  { top: '84%', left: '48%', size: 8, delay: '2s' },
  { top: '78%', left: '93%', size: 9, delay: '0.2s' },
  { top: '92%', left: '30%', size: 10, delay: '1.7s' },
  { top: '95%', left: '70%', size: 8, delay: '0.5s', tone: 'pink' },
  { top: '97%', left: '10%', size: 9, delay: '2.3s' },
];

async function playToddlerVoice(say, text, cacheKey, minimumPause = 850) {
  const startedAt = Date.now();
  const playback = say(text, cacheKey);
  if (!playback) {
    await pause(180);
    return;
  }
  const maxWait = Math.max(minimumPause, 1400);
  await Promise.race([playback, pause(maxWait)]);
  const elapsed = Date.now() - startedAt;
  await pause(Math.max(180, minimumPause - elapsed));
}

function buildAnimalMatchRounds() {
  return shuffle(animalMatchRounds).map((round) => ({
    ...round,
    choices: placeAnswerAwayFromFirst(round.choices, round.answer),
  }));
}

function placeAnswerAwayFromFirst(choices, answer) {
  const ordered = shuffle(choices);
  if (ordered[0] !== answer) return ordered;
  const answerIndex = ordered.findIndex((choice) => choice === answer);
  const swapIndex = ordered.length > 1 ? Math.floor(Math.random() * (ordered.length - 1)) + 1 : 0;
  [ordered[answerIndex], ordered[swapIndex]] = [ordered[swapIndex], ordered[answerIndex]];
  return ordered;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false
      : false
  ));

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const update = () => setReduced(query.matches);
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  return reduced;
}

function AnimalAdventureSection({ onComplete, progress, reward }) {
  const [activeGameId, setActiveGameId] = useState('match');
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('animal_adventure_voice') !== 'off');
  const reducedMotion = useReducedMotion();
  const completedAnimalGames = progress.completedAnimalGames || [];
  const stageRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('animal_adventure_voice', voiceOn ? 'on' : 'off');
  }, [voiceOn]);

  const say = useCallback((text, cacheKey) => {
    if (!voiceOn || !text) return null;
    return playTypecastOliverText(text, reward, null, cacheKey || `animal-${stableAudioKey(text)}`);
  }, [reward, voiceOn]);

  const playWord = useCallback((wordId, fallbackText) => {
    if (!voiceOn) return null;
    return playOliverAudio(`/voice-audio/words/${wordId}-oliver.wav`, () => {}, null, fallbackText);
  }, [voiceOn]);

  const completeAnimalGame = (gameId) => {
    const game = animalGames.find((item) => item.id === gameId);
    onComplete('completedAnimalGames', gameId, null, `${game?.title || 'Animal game'} complete!`);
  };

  const activeGame = animalGames.find((game) => game.id === activeGameId) || animalGames[0];

  const selectAnimalGame = (game) => {
    setActiveGameId(game.id);
    say(game.instruction, `animal-game-${game.id}`);
    stageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Section title="Toddler Section" subtitle="Tiny games for little explorers aged 2 to 5. Tap, count, match, and sing along.">
      <div className="animal-adventure-shell">
        <div className="animal-topbar">
          <div>
            <p className="kicker">Preschool Play</p>
            <h3>Friendly animals, gentle Oliver voice</h3>
          </div>
          <button
            className={`sound-toggle ${voiceOn ? 'active' : ''}`}
            onClick={() => setVoiceOn((current) => {
              if (current) stopSpeech();
              return !current;
            })}
            aria-pressed={voiceOn}
          >
            <Volume2 size={18} />
            {voiceOn ? 'Voice On' : 'Voice Off'}
          </button>
        </div>

        <div className="animal-game-tabs" aria-label="Toddler Section games">
          {animalGames.map((game) => (
            <button
              key={game.id}
              className={`theme-${game.color} ${activeGameId === game.id ? 'active' : ''}`}
              onClick={() => selectAnimalGame(game)}
            >
              <AnimalGameBadge gameId={game.id} />
              <span>{game.title}</span>
              {completedAnimalGames.includes(game.id) && <Check size={16} />}
            </button>
          ))}
        </div>

        <article ref={stageRef} className="animal-game-stage">
          <GameHeader title={activeGame.title} instruction={activeGame.instruction} onSpeak={() => say(activeGame.instruction, `animal-game-${activeGame.id}`)} />
          {activeGameId === 'match' && <AnimalMatchGame say={say} onComplete={() => completeAnimalGame('match')} reducedMotion={reducedMotion} />}
          {activeGameId === 'feed' && <FeedAnimalsGame say={say} onComplete={() => completeAnimalGame('feed')} reducedMotion={reducedMotion} />}
          {activeGameId === 'count' && <CountAnimalsGame say={say} onComplete={() => completeAnimalGame('count')} />}
          {activeGameId === 'pairs' && <AnimalPairsGame say={say} onComplete={() => completeAnimalGame('pairs')} reducedMotion={reducedMotion} />}
          {activeGameId === 'family-song' && <FamilySingAlongGame playWord={playWord} onComplete={() => completeAnimalGame('family-song')} reducedMotion={reducedMotion} />}
        </article>
      </div>
    </Section>
  );
}

function AnimalGameBadge({ gameId }) {
  return (
    <span className={`animal-game-badge badge-${gameId}`} aria-hidden="true">
      <span />
    </span>
  );
}

function GameHeader({ title, instruction, onSpeak }) {
  return (
    <div className="preschool-game-header">
      <div>
        <p className="kicker">Tap to play</p>
        <h3>{title}</h3>
        <p>{instruction}</p>
      </div>
      <button className="audio-button" onClick={onSpeak}>
        <Volume2 size={20} />
        Hear
      </button>
    </div>
  );
}

function ProgressIndicator({ current, total }) {
  return (
    <div className="preschool-progress" aria-label={`${current} of ${total} complete`}>
      {Array.from({ length: total }, (_, index) => (
        <span key={index} className={index < current ? 'filled' : ''} />
      ))}
    </div>
  );
}

function SuccessAnimation({ message }) {
  if (!message) return null;
  return (
    <div className="success-animation" role="status">
      <Check size={24} />
      {message}
    </div>
  );
}

function GameCompleteFeedback({ message, buttonLabel, onReplay }) {
  return (
    <div className="game-complete-feedback" role="status">
      <span className="game-complete-icon"><Star size={22} fill="currentColor" /></span>
      <p>{message} Tap <strong>{buttonLabel}</strong> to keep playing.</p>
      <button className="primary-action" onClick={onReplay}>
        <RefreshCw size={18} />
        {buttonLabel}
      </button>
    </div>
  );
}

function getAnimal(id) {
  return animalCharacters.find((animal) => animal.id === id) || animalCharacters[0];
}

function AnimalCard({ animal, onSelect, selected = false, happy = false, small = false, disabled = false }) {
  const data = typeof animal === 'string' ? getAnimal(animal) : animal;

  return (
    <button
      className={`animal-card ${selected ? 'selected' : ''} ${happy ? 'happy' : ''} ${small ? 'small' : ''}`}
      style={{ '--animal-start': data.colors[0], '--animal-end': data.colors[1] }}
      onClick={() => onSelect?.(data)}
      disabled={disabled}
      aria-label={data.name}
    >
      <AnimalIllustration animal={data} happy={happy} />
      <span className="animal-card-label">
        <strong>{data.shortName}</strong>
        <small>{data.animal}</small>
      </span>
    </button>
  );
}

function AnimalIllustration({ animal, happy = false }) {
  const image = animalImages[animal.id];

  return (
    <img
      className={`animal-illustration animal-${animal.id} ${happy ? 'happy' : ''}`}
      src={image}
      alt={`${animal.name} ${animal.animal.toLowerCase()} illustration`}
      loading="lazy"
      draggable="false"
    />
  );
}

function AnimalMatchGame({ say, onComplete, reducedMotion }) {
  const [rounds, setRounds] = useState(() => buildAnimalMatchRounds());
  const [roundIndex, setRoundIndex] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [happyAnimal, setHappyAnimal] = useState('');
  const [finished, setFinished] = useState(false);
  const [locked, setLocked] = useState(false);
  const round = rounds[roundIndex % rounds.length];

  const speakPrompt = useCallback(() => {
    say(round.prompt, `animal-match-${round.id}`);
  }, [round.id, round.prompt, say]);

  useEffect(() => {
    const promptDelay = window.setTimeout(speakPrompt, reducedMotion ? 80 : 350);
    return () => window.clearTimeout(promptDelay);
  }, [reducedMotion, speakPrompt]);

  const choose = async (animal) => {
    if (finished || locked) return;
    setLocked(true);
    if (animal.id === round.answer) {
      const praise = animalPraise[roundIndex % animalPraise.length];
      setFeedback(praise);
      setHappyAnimal(animal.id);
      await playToddlerVoice(say, praise, `animal-praise-${stableAudioKey(praise)}`, reducedMotion ? 360 : 1050);
      if (roundIndex >= rounds.length - 1) {
        setFinished(true);
        setFeedback('');
        onComplete();
      } else {
        setRoundIndex((current) => current + 1);
        setFeedback('');
        setHappyAnimal('');
      }
    } else {
      setFeedback('Almost! Try again.');
      await playToddlerVoice(say, 'Almost! Try again.', 'animal-almost-try-again', reducedMotion ? 360 : 950);
    }
    setLocked(false);
  };

  const playAgain = () => {
    setRounds(buildAnimalMatchRounds());
    setRoundIndex(0);
    setFinished(false);
    setFeedback('');
    setHappyAnimal('');
    setLocked(false);
  };

  return (
    <div className="preschool-game">
      <ProgressIndicator current={finished ? rounds.length : roundIndex} total={rounds.length} />
      <div className="prompt-row">
        <h4>{round.prompt}</h4>
        <button type="button" className="audio-button compact" onClick={speakPrompt} aria-label="Hear this again">
          <Volume2 size={18} />
          Hear
        </button>
      </div>
      <div className="animal-choice-grid">
        {round.choices.map((id) => (
          <AnimalCard key={id} animal={id} happy={happyAnimal === id} onSelect={choose} />
        ))}
      </div>
      <SuccessAnimation message={feedback} />
      {finished && <GameCompleteFeedback message="MashaAllah! You matched them all." buttonLabel="Play Again" onReplay={playAgain} />}
    </div>
  );
}

function FoodShape({ food, draggable = false, onDragStart, onClick, selected = false }) {
  return (
    <button
      className={`food-shape ${selected ? 'selected' : ''}`}
      style={{ '--food-color': food.color }}
      draggable={draggable}
      onDragStart={(event) => onDragStart?.(event, food)}
      onClick={() => onClick?.(food)}
      aria-label={food.foodName}
    >
      <FoodIllustration foodId={food.foodId} color={food.color} />
      <strong>{food.foodName}</strong>
    </button>
  );
}

function FoodIllustration({ foodId, color }) {
  return (
    <svg className="food-illustration" viewBox="0 0 96 96" aria-hidden="true">
      {foodId === 'carrot' && (
        <>
          <path d="M55 20c-12 4-25 19-37 50 31-10 49-23 55-37 4-10-7-17-18-13Z" fill={color} stroke="#173d5c" strokeWidth="6" strokeLinejoin="round" />
          <path d="M58 21c2-12 10-15 21-14-3 11-9 17-21 14ZM56 25c-8-8-7-17 0-25 8 8 8 18 0 25Z" fill="#62d4a6" stroke="#173d5c" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}
      {foodId === 'fish' && (
        <>
          <ellipse cx="47" cy="50" rx="28" ry="20" fill={color} stroke="#173d5c" strokeWidth="6" />
          <path d="M72 50 92 34v32Z" fill="#9be7ff" stroke="#173d5c" strokeWidth="6" strokeLinejoin="round" />
          <circle cx="38" cy="45" r="4" fill="#173d5c" />
          <path d="M18 50c6-7 14-7 20 0-6 7-14 7-20 0Z" fill="#fffdf7" stroke="#173d5c" strokeWidth="5" />
        </>
      )}
      {foodId === 'grass' && (
        <>
          <path d="M15 76C25 48 30 35 42 20c1 22 0 38-5 56M40 76c7-31 18-49 35-62-3 31-11 50-22 62M61 76c4-20 10-33 24-46 0 20-4 35-11 46" fill="none" stroke="#173d5c" strokeWidth="8" strokeLinecap="round" />
          <path d="M14 76h70" stroke={color} strokeWidth="13" strokeLinecap="round" />
        </>
      )}
      {foodId === 'banana' && (
        <>
          <path d="M21 35c22 35 48 43 66 18-8 32-38 46-68 7-7-9-4-20 2-25Z" fill={color} stroke="#173d5c" strokeWidth="6" strokeLinejoin="round" />
          <path d="M21 35c-2-8 3-15 12-18" stroke="#8b5a2b" strokeWidth="7" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

function FeedAnimalsGame({ say, onComplete, reducedMotion }) {
  const [fed, setFed] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [feedback, setFeedback] = useState('Pick food.');
  const [happyAnimal, setHappyAnimal] = useState('');
  const [locked, setLocked] = useState(false);
  const [finished, setFinished] = useState(false);
  const [foods, setFoods] = useState(() => shuffle(feedingPairs));
  const [animalTargets, setAnimalTargets] = useState(() => shuffle(feedingPairs));

  const tryFeed = async (animalId, foodId) => {
    const pair = feedingPairs.find((item) => item.animalId === animalId);
    if (locked || finished || !pair || fed.includes(pair.id)) return;
    setLocked(true);
    if (pair.foodId === foodId) {
      const nextFed = [...fed, pair.id];
      setFed(nextFed);
      setHappyAnimal(animalId);
      setFeedback('Bismillah!');
      await playToddlerVoice(say, 'Bismillah!', 'animal-bismillah', reducedMotion ? 360 : 950);
      if (nextFed.length === feedingPairs.length) {
        await pause(reducedMotion ? 120 : 300);
        setFeedback('');
        setFinished(true);
        onComplete();
      }
    } else {
      setFeedback('Almost! Try again.');
      await playToddlerVoice(say, 'Almost! Try again.', 'animal-almost-try-again', reducedMotion ? 360 : 950);
    }
    setSelectedFood(null);
    setLocked(false);
  };

  const dropFood = (event, animalId) => {
    event.preventDefault();
    tryFeed(animalId, event.dataTransfer.getData('food-id'));
  };

  const playAgain = () => {
    setFed([]);
    setSelectedFood(null);
    setFeedback('Pick food.');
    setHappyAnimal('');
    setFinished(false);
    setFoods(shuffle(feedingPairs));
    setAnimalTargets(shuffle(feedingPairs));
  };

  return (
    <div className="preschool-game">
      <ProgressIndicator current={fed.length} total={feedingPairs.length} />
      <div className="food-row">
        {foods.map((food) => (
          <FoodShape key={food.foodId} food={food} draggable selected={selectedFood === food.foodId} onDragStart={(event, item) => event.dataTransfer.setData('food-id', item.foodId)} onClick={(item) => setSelectedFood(item.foodId)} />
        ))}
      </div>
      <div className="animal-choice-grid">
        {animalTargets.map((pair) => (
          <div
            key={pair.id}
            className={`animal-drop-zone ${fed.includes(pair.id) ? 'fed' : ''}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => dropFood(event, pair.animalId)}
          >
            <AnimalCard animal={pair.animalId} happy={happyAnimal === pair.animalId || fed.includes(pair.id)} onSelect={() => selectedFood && tryFeed(pair.animalId, selectedFood)} disabled={fed.includes(pair.id) || locked || finished} />
          </div>
        ))}
      </div>
      <SuccessAnimation message={feedback} />
      {finished && <GameCompleteFeedback message="MashaAllah! Everyone is fed." buttonLabel="Play Again" onReplay={playAgain} />}
    </div>
  );
}

function CountAnimalsGame({ say, onComplete }) {
  const [rounds, setRounds] = useState(() => shuffle(countAnimalRounds));
  const [roundIndex, setRoundIndex] = useState(0);
  const [tapped, setTapped] = useState([]);
  const [finished, setFinished] = useState(false);
  const [locked, setLocked] = useState(false);
  const round = rounds[roundIndex % rounds.length];
  const items = Array.from({ length: round.count }, (_, index) => `${round.id}-${index}`);

  const tapAnimal = async (id) => {
    if (locked || tapped.includes(id) || finished) return;
    setLocked(true);
    const next = [...tapped, id];
    setTapped(next);
    await playToddlerVoice(say, numberWords[next.length - 1], `animal-count-${next.length}`, 520);
    if (next.length === round.count) {
      await playToddlerVoice(say, 'MashaAllah!', 'animal-mashaallah', 800);
      if (roundIndex >= rounds.length - 1) {
        setFinished(true);
        onComplete();
      } else {
        setRoundIndex((current) => current + 1);
        setTapped([]);
      }
    }
    setLocked(false);
  };

  const playAgain = () => {
    setRounds(shuffle(countAnimalRounds));
    setRoundIndex(0);
    setTapped([]);
    setFinished(false);
    setLocked(false);
  };

  return (
    <div className="preschool-game">
      <ProgressIndicator current={finished ? rounds.length : roundIndex} total={rounds.length} />
      <h4>Count them</h4>
      <div className="count-animal-grid">
        {items.map((id, index) => (
          <button key={id} className={`count-animal ${tapped.includes(id) ? 'counted' : ''}`} onClick={() => tapAnimal(id)}>
            <AnimalIllustration animal={getAnimal(round.animalId)} happy={tapped.includes(id)} />
            <span>{tapped.includes(id) ? numberWords[tapped.indexOf(id)] : ''}</span>
          </button>
        ))}
      </div>
      <SuccessAnimation message={!finished && tapped.length === round.count ? 'MashaAllah!' : ''} />
      {finished && <GameCompleteFeedback message="MashaAllah! You counted them all." buttonLabel="Play Again" onReplay={playAgain} />}
    </div>
  );
}

function AnimalPairsGame({ say, onComplete, reducedMotion }) {
  const [level, setLevel] = useState(2);
  const [cards, setCards] = useState(() => buildAnimalPairCards(2));
  const [open, setOpen] = useState([]);
  const [matched, setMatched] = useState([]);
  const [locked, setLocked] = useState(false);
  const [finished, setFinished] = useState(false);
  const [message, setMessage] = useState('Match the pairs.');

  const chooseCard = (card) => {
    if (locked || finished || matched.includes(card.cardId) || open.some((item) => item.cardId === card.cardId)) return;
    const nextOpen = [...open, card];
    setOpen(nextOpen);

    if (nextOpen.length === 2) {
      setLocked(true);
      window.setTimeout(async () => {
        if (nextOpen[0].animalId === nextOpen[1].animalId) {
          const nextMatched = [...matched, nextOpen[0].cardId, nextOpen[1].cardId];
          setMatched(nextMatched);
          setMessage('Well done!');
          await playToddlerVoice(say, 'Well done!', 'animal-well-done', reducedMotion ? 360 : 900);
          if (nextMatched.length === cards.length) {
            if (level >= 4) {
              setMessage('');
              setFinished(true);
              onComplete();
              await playToddlerVoice(say, 'MashaAllah!', 'animal-mashaallah', reducedMotion ? 360 : 900);
            } else {
              const nextLevel = level + 1;
              setLevel(nextLevel);
              setCards(buildAnimalPairCards(nextLevel));
              setMatched([]);
              setMessage('More pairs!');
            }
          }
        } else {
          setMessage('Almost! Try again.');
          await playToddlerVoice(say, 'Almost! Try again.', 'animal-almost-try-again', reducedMotion ? 360 : 950);
        }
        setOpen([]);
        setLocked(false);
      }, reducedMotion ? 180 : 650);
    }
  };

  const playAgain = () => {
    setLevel(2);
    setCards(buildAnimalPairCards(2));
    setOpen([]);
    setMatched([]);
    setLocked(false);
    setFinished(false);
    setMessage('Match the pairs.');
  };

  return (
    <div className="preschool-game ark-game">
      <ProgressIndicator current={Math.min(level - 2, 2) + (matched.length === cards.length ? 1 : 0)} total={3} />
      <div className={`animal-pair-grid level-${level}`}>
        {cards.map((card) => {
          const visible = matched.includes(card.cardId) || open.some((item) => item.cardId === card.cardId);
          return (
            <button key={card.cardId} className={`animal-pair-card ${visible ? 'visible' : ''}`} onClick={() => chooseCard(card)} aria-label={visible ? getAnimal(card.animalId).name : 'Hidden animal card'}>
              {visible ? <AnimalIllustration animal={getAnimal(card.animalId)} happy={matched.includes(card.cardId)} /> : <span className="pair-card-back" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      <SuccessAnimation message={message} />
      {finished && <GameCompleteFeedback message="MashaAllah! You matched every pair." buttonLabel="Play Again" onReplay={playAgain} />}
    </div>
  );
}

function buildAnimalPairCards(pairCount) {
  return shuffle(['cat', 'lion', 'lamb', 'bird'].slice(0, pairCount).flatMap((animalId) => [
    { cardId: `${animalId}-a`, animalId },
    { cardId: `${animalId}-b`, animalId },
  ]));
}

function FamilySingAlongGame({ playWord, onComplete, reducedMotion }) {
  const [verseIndex, setVerseIndex] = useState(0);
  const [sungIds, setSungIds] = useState([]);
  const [locked, setLocked] = useState(false);
  const [finished, setFinished] = useState(false);
  const [message, setMessage] = useState(`Tap ${familySingAlongVerses[0].role} Duck to start the song!`);
  const activeVerse = familySingAlongVerses[verseIndex];

  const singVerse = async (verse) => {
    if (locked || finished || verse.id !== activeVerse.id) return;
    setLocked(true);
    setMessage(`${verse.role} Duck sings ${verse.phrase}!`);
    const playback = playWord(verse.wordId, verse.phrase);
    await (playback || pause(reducedMotion ? 200 : 700));
    setSungIds((current) => [...current, verse.id]);

    if (verseIndex >= familySingAlongVerses.length - 1) {
      await pause(reducedMotion ? 150 : 400);
      setMessage('Everyone sings together — Jazakallah Khair!');
      const finalePlayback = playWord(familySongFinale.wordId, familySongFinale.phrase);
      await (finalePlayback || pause(reducedMotion ? 200 : 700));
      setMessage('');
      setFinished(true);
      onComplete();
    } else {
      const nextVerse = familySingAlongVerses[verseIndex + 1];
      setVerseIndex((current) => current + 1);
      setMessage(`Now tap ${nextVerse.role} Duck!`);
    }
    setLocked(false);
  };

  const playAgain = () => {
    setVerseIndex(0);
    setSungIds([]);
    setFinished(false);
    setMessage(`Tap ${familySingAlongVerses[0].role} Duck to start the song!`);
  };

  return (
    <div className="preschool-game">
      <ProgressIndicator current={finished ? familySingAlongVerses.length : sungIds.length} total={familySingAlongVerses.length} />
      <h4>Sing With the Family</h4>
      <div className={`family-song-row ${finished ? 'dancing' : ''}`}>
        {familySingAlongVerses.map((verse, index) => {
          const isSung = sungIds.includes(verse.id) || finished;
          const isActive = !finished && verse.id === activeVerse.id;
          return (
            <button
              key={verse.id}
              className={`family-member family-${verse.size} ${isActive ? 'active' : ''} ${isSung ? 'happy' : ''}`}
              style={{ '--dance-delay': `${index * 90}ms` }}
              onClick={() => singVerse(verse)}
              disabled={!isActive}
              aria-label={`${verse.role} Duck sings ${verse.phrase}`}
            >
              <img src={animalDuck} className="family-member-illustration" alt="" draggable="false" />
              <span className="family-member-label">{verse.role}</span>
              {isSung && <Check size={16} />}
            </button>
          );
        })}
      </div>
      <SuccessAnimation message={message} />
      {finished && <GameCompleteFeedback message="Jazakallah Khair! Everyone sang together." buttonLabel="Sing Again" onReplay={playAgain} />}
    </div>
  );
}

function GameIcon({ gameId, compact = false }) {
  const iconMap = {
    'snakes-ladders': ShieldCheck,
    'memory-match': Star,
    'islamic-quiz': BookOpen,
    'prophet-quiz': Moon,
    'matching-game': Check,
    'letter-match': Baby,
    'alphabet-tracing': Paintbrush,
    'good-deed-sort': Heart,
    'salah-steps': ShieldCheck,
    'puzzle-game': RefreshCw,
    'tic-tac-toe': Gamepad2,
  };
  const Icon = iconMap[gameId] || Gamepad2;

  return (
    <span className={compact ? 'game-icon compact' : 'game-icon'} aria-hidden="true">
      <Icon size={compact ? 22 : 34} strokeWidth={2.5} />
    </span>
  );
}

function DecorIcon({ type = 'star', size = 34, className = '' }) {
  const iconMap = {
    arabic: Baby,
    book: BookOpen,
    check: Check,
    create: Palette,
    deed: Heart,
    game: Gamepad2,
    heart: Heart,
    home: Home,
    kindness: Heart,
    moon: Moon,
    music: Music,
    quran: BookOpen,
    shield: ShieldCheck,
    star: Star,
    video: Clapperboard,
    water: Volume2,
  };
  const Icon = iconMap[type] || Star;

  return (
    <span className={`decor-icon ${className}`} aria-hidden="true">
      <Icon size={size} strokeWidth={2.5} />
    </span>
  );
}

function SnakesLaddersGame({ onWin }) {
  const [position, setPosition] = useState(1);
  const [roll, setRoll] = useState(null);
  const [message, setMessage] = useState('Answer the question to roll and move.');
  const [questionPool, setQuestionPool] = useState(() => shuffle(snakeQuestions));
  const [questionIndex, setQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [won, setWon] = useState(false);
  const jumps = { 3: 8, 6: 11, 10: 14, 13: 9 };
  const challenge = questionPool[questionIndex % questionPool.length];
  const challengeOptions = useMemo(() => shuffle(challenge.options), [challenge]);

  const answerQuestion = (option) => {
    if (won) return;
    const correct = option === challenge.answer;
    const nextScore = correct ? score + 1 : score;
    setScore(nextScore);
    setQuestionIndex((current) => current + 1);

    if (!correct) {
      setRoll(null);
      setMessage(`Good try. The answer was ${challenge.answer}. Stay on ${position}.`);
      return;
    }

    const dice = Math.floor(Math.random() * 3) + 1;
    const next = Math.min(16, position + dice);
    const jumped = jumps[next] || next;
    setRoll(dice);
    setPosition(jumped);

    if (jumped >= 16) {
      setWon(true);
      setMessage(`You reached the moon garden! Score: ${nextScore}/${questionIndex + 1}`);
      onWin();
    } else if (jumps[next] && jumps[next] > next) {
      setMessage('Correct! You climbed a ladder of good deeds.');
    } else if (jumps[next] && jumps[next] < next) {
      setMessage('Correct, but this square slides back. Try again with sabr.');
    } else {
      setMessage('Correct! Nice move. Keep going!');
    }
  };

  const reset = () => {
    setPosition(1);
    setRoll(null);
    setQuestionPool(shuffle(snakeQuestions));
    setQuestionIndex(0);
    setScore(0);
    setWon(false);
    setMessage('Answer the question to roll and move.');
  };

  return (
    <div className="mini-game">
      <div className="board-grid">
        {Array.from({ length: 16 }, (_, index) => {
          const number = 16 - index;
          return (
            <div key={number} className={`board-cell ${position === number ? 'player-here' : ''}`}>
              <span>{number}</span>
              {number === 16 && <DecorIcon type="moon" size={20} className="board-symbol" />}
              {jumps[number] && jumps[number] > number && <DecorIcon type="star" size={20} className="board-symbol" />}
              {jumps[number] && jumps[number] < number && <DecorIcon type="shield" size={20} className="board-symbol" />}
              {position === number && <strong><DecorIcon type="star" size={22} /></strong>}
            </div>
          );
        })}
      </div>
      <div className="game-controls">
        <p>{message}</p>
        {!won && (
          <div className="snake-question">
            <h4>{challenge.question}</h4>
            <div className="answer-grid">
              {challengeOptions.map((option) => (
                <button key={option} onClick={() => answerQuestion(option)}>{option}</button>
              ))}
            </div>
          </div>
        )}
        {roll && <strong>Dice: {roll}</strong>}
        <strong>Score: {score}/{questionIndex}</strong>
        <button onClick={reset}>Reset</button>
      </div>
    </div>
  );
}

function MemoryMatchGame({ onWin }) {
  const [cards, setCards] = useState(() => shuffle([...memoryDeck, ...memoryDeck]).map((value, index) => ({ id: `${value}-${index}`, value, matched: false })));
  const [open, setOpen] = useState([]);
  const [locked, setLocked] = useState(false);
  const [won, setWon] = useState(false);

  const pickCard = (card) => {
    if (locked || won || card.matched || open.find((item) => item.id === card.id)) return;
    const nextOpen = [...open, card];
    setOpen(nextOpen);

    if (nextOpen.length === 2) {
      setLocked(true);
      window.setTimeout(() => {
        if (nextOpen[0].value === nextOpen[1].value) {
          setCards((current) => {
            const updated = current.map((item) => nextOpen.some((openCard) => openCard.id === item.id) ? { ...item, matched: true } : item);
            if (updated.every((item) => item.matched)) {
              setWon(true);
              onWin();
            }
            return updated;
          });
        }
        setOpen([]);
        setLocked(false);
      }, 650);
    }
  };

  const reset = () => {
    setCards(shuffle([...memoryDeck, ...memoryDeck]).map((value, index) => ({ id: `${value}-${index}`, value, matched: false })));
    setOpen([]);
    setLocked(false);
    setWon(false);
  };

  return (
    <div className="mini-game">
      <div className="memory-grid">
        {cards.map((card) => {
          const visible = card.matched || open.find((item) => item.id === card.id);
          return (
            <button key={card.id} className={visible ? 'revealed' : ''} onClick={() => pickCard(card)}>
              {visible ? <DecorIcon type={card.value} size={38} /> : <DecorIcon type="star" size={38} className="memory-hidden-icon" />}
            </button>
          );
        })}
      </div>
      <div className="game-controls">
        <p>{won ? 'All pairs matched!' : 'Tap two cards to find matching pairs.'}</p>
        <button onClick={reset}>Shuffle</button>
      </div>
    </div>
  );
}

function IslamicQuizGame({ onWin }) {
  const roundSize = 8;
  const [roundQuestions, setRoundQuestions] = useState(() => shuffle(quizQuestions).slice(0, roundSize));
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [answered, setAnswered] = useState(false);
  const question = roundQuestions[index];
  const options = useMemo(() => shuffle(question.options), [question]);

  const answer = (option) => {
    if (answered || done) return;
    setAnswered(true);
    const nextScore = option === question.answer ? score + 1 : score;
    setScore(nextScore);

    window.setTimeout(() => {
      if (index === roundQuestions.length - 1) {
        setDone(true);
        if (nextScore >= 5) onWin();
      } else {
        setIndex(index + 1);
        setAnswered(false);
      }
    }, 350);
  };

  const reset = () => {
    setRoundQuestions(shuffle(quizQuestions).slice(0, roundSize));
    setIndex(0);
    setScore(0);
    setDone(false);
    setAnswered(false);
  };

  return (
    <div className="mini-game quiz-game">
      {done ? (
        <>
          <h4>You scored {score}/{roundQuestions.length}</h4>
          <p>{score >= 5 ? 'Lovely work!' : 'Try once more and see what you remember.'}</p>
          <button onClick={reset}>Play Again</button>
        </>
      ) : (
        <>
          <p className="quiz-count">Question {index + 1} of {roundQuestions.length}</p>
          <h4>{question.question}</h4>
          <div className="answer-grid">
            {options.map((option) => (
              <button key={option} disabled={answered} onClick={() => answer(option)}>{option}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProphetQuizGame({ onWin }) {
  const roundSize = 8;
  const [roundQuestions, setRoundQuestions] = useState(() => shuffle(prophetQuizQuestions).slice(0, roundSize));
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [feedback, setFeedback] = useState('Pick the best answer.');
  const question = roundQuestions[index];
  const options = useMemo(() => shuffle(question.options), [question]);

  const answer = (option) => {
    if (answered || done) return;
    setAnswered(true);
    const correct = option === question.answer;
    const nextScore = correct ? score + 1 : score;
    setScore(nextScore);
    setFeedback(correct ? 'Correct! Lovely remembering.' : `Good try. The answer is ${question.answer}.`);

    window.setTimeout(() => {
      if (index === roundQuestions.length - 1) {
        setDone(true);
        if (nextScore >= 5) onWin();
      } else {
        setIndex(index + 1);
        setFeedback('Pick the best answer.');
        setAnswered(false);
      }
    }, 650);
  };

  const reset = () => {
    setRoundQuestions(shuffle(prophetQuizQuestions).slice(0, roundSize));
    setIndex(0);
    setScore(0);
    setDone(false);
    setAnswered(false);
    setFeedback('Pick the best answer.');
  };

  return (
    <div className="mini-game quiz-game prophet-quiz">
      {done ? (
        <>
          <h4>Prophet Quiz Score: {score}/{roundQuestions.length}</h4>
          <p>{score >= 5 ? 'Wonderful work! You remembered many stories.' : 'Read a few stories and try again.'}</p>
          <button onClick={reset}>Play Again</button>
        </>
      ) : (
        <>
          <p className="quiz-count">Prophet Question {index + 1} of {roundQuestions.length}</p>
          <h4>{question.question}</h4>
          <div className="answer-grid">
            {options.map((option) => (
              <button key={option} disabled={answered} onClick={() => answer(option)}>{option}</button>
            ))}
          </div>
          <p className="quiz-feedback">{feedback}</p>
        </>
      )}
    </div>
  );
}

function MatchingGame({ onWin }) {
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [matched, setMatched] = useState([]);
  const [message, setMessage] = useState('Choose a phrase, then its meaning.');
  const [rightOptions] = useState(() => shuffle(matchingPairs));

  const chooseRight = (pair) => {
    if (!selectedLeft) {
      setMessage('Pick a phrase first.');
      return;
    }
    if (selectedLeft.left === pair.left) {
      const nextMatched = [...matched, pair.left];
      setMatched(nextMatched);
      setSelectedLeft(null);
      setMessage('Correct match!');
      if (nextMatched.length === matchingPairs.length) onWin();
    } else {
      setMessage('Almost. Try another meaning.');
    }
  };

  return (
    <div className="mini-game">
      <p>{message}</p>
      <div className="match-columns">
        <div>
          {matchingPairs.map((pair) => (
            <button
              key={pair.left}
              disabled={matched.includes(pair.left)}
              className={selectedLeft?.left === pair.left ? 'selected-match' : ''}
              onClick={() => setSelectedLeft(pair)}
            >
              {pair.left}
            </button>
          ))}
        </div>
        <div>
          {rightOptions.map((pair) => (
            <button key={pair.right} disabled={matched.includes(pair.left)} onClick={() => chooseRight(pair)}>
              {pair.right}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LetterMatchGame({ onWin }) {
  const [roundLetters, setRoundLetters] = useState(() => shuffle(arabicLetters));
  const [index, setIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selected, setSelected] = useState('');
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState('Choose the sound that matches the letter.');
  const current = roundLetters[index];
  const options = useMemo(() => {
    const wrong = shuffle(arabicLetters.filter((letter) => letter.id !== current.id)).slice(0, 3);
    return shuffle([current, ...wrong].map((letter) => letter.transliteration));
  }, [current]);

  const choose = (option) => {
    if (answered || done) return;
    const isCorrect = option === current.transliteration;
    const nextScore = isCorrect ? score + 1 : score;
    setSelected(option);
    setAnswered(true);
    setScore(nextScore);
    setFeedback(isCorrect ? 'Correct!' : `Good try. This letter is ${current.transliteration}.`);

    window.setTimeout(() => {
      if (index === roundLetters.length - 1) {
        setDone(true);
        setFeedback(`Final score: ${nextScore}/${roundLetters.length}`);
        if (nextScore >= Math.ceil(roundLetters.length * 0.7)) onWin();
      } else {
        setIndex(index + 1);
        setSelected('');
        setAnswered(false);
        setFeedback('Choose the sound that matches the letter.');
      }
    }, 750);
  };

  const reset = () => {
    setRoundLetters(shuffle(arabicLetters));
    setIndex(0);
    setScore(0);
    setAnswered(false);
    setSelected('');
    setDone(false);
    setFeedback('Choose the sound that matches the letter.');
  };

  return (
    <div className="mini-game letter-match-game">
      {done ? (
        <>
          <h4>Arabic Letter Score: {score}/{roundLetters.length}</h4>
          <p>{score >= Math.ceil(roundLetters.length * 0.7) ? 'Wonderful letter work!' : 'Good practice. Try again and beat your score.'}</p>
        </>
      ) : (
        <>
          <p className="quiz-count">Letter {index + 1} of {roundLetters.length}</p>
          <span className="arabic-letter" dir="rtl" lang="ar">{current.letter}</span>
          <p>Which sound matches this letter?</p>
          <div className="answer-grid">
            {options.map((option) => (
              <button
                key={option}
                className={`${answered && option === current.transliteration ? 'correct-answer' : ''} ${selected === option && option !== current.transliteration ? 'wrong-answer' : ''}`}
                disabled={answered}
                onClick={() => choose(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="game-controls">
        <p>{feedback}</p>
        <strong>Score: {score}/{done ? roundLetters.length : index + (answered ? 1 : 0)}</strong>
        <button onClick={reset}>Restart</button>
      </div>
    </div>
  );
}

function AlphabetTracingGame({ onWin }) {
  const traceLetters = arabicLetters;
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [index, setIndex] = useState(0);
  const [traced, setTraced] = useState([]);
  const [message, setMessage] = useState('Trace the big letter, then tap Done.');
  const current = traceLetters[index];

  useEffect(() => {
    clearTraceCanvas();
  }, [index]);

  const clearTraceCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 18;
    ctx.strokeStyle = '#1fb8a6';
  };

  const tracePoint = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = event.touches?.[0];
    const clientX = touch ? touch.clientX : event.clientX;
    const clientY = touch ? touch.clientY : event.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvasRef.current.width,
      y: ((clientY - rect.top) / rect.height) * canvasRef.current.height,
    };
  };

  const start = (event) => {
    event.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const point = tracePoint(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const point = tracePoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stop = () => {
    drawing.current = false;
  };

  const markDone = () => {
    const nextTraced = traced.includes(current.id) ? traced : [...traced, current.id];
    setTraced(nextTraced);

    if (nextTraced.length >= traceLetters.length) {
      setMessage('All letters traced. Wonderful writing practice!');
      onWin();
      return;
    }

    const nextIndex = (index + 1) % traceLetters.length;
    setIndex(nextIndex);
    setMessage(`${current.transliteration} traced. Try ${traceLetters[nextIndex].transliteration} next.`);
  };

  const reset = () => {
    setIndex(0);
    setTraced([]);
    setMessage('Trace the big letter, then tap Done.');
  };

  return (
    <div className="mini-game tracing-game">
      <div className="tracing-header">
        <div>
          <span className="arabic-letter compact-letter" dir="rtl" lang="ar">{current.letter}</span>
          <h4>{current.transliteration}</h4>
        </div>
        <div className="game-controls">
          <p>{message}</p>
          <strong>{traced.length}/{traceLetters.length} traced</strong>
        </div>
      </div>
      <div className="trace-board game-trace-board">
        <span className="trace-guide" dir="rtl" lang="ar">{current.letter}</span>
        <canvas
          ref={canvasRef}
          width="360"
          height="360"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={stop}
          aria-label={`Trace ${current.transliteration}`}
        />
      </div>
      <div className="trace-actions inline-actions">
        <button onClick={() => playOliverAudio(`/voice-audio/letters/${current.id}-oliver.wav`, () => {}, null, current.voiceText)}>
          <Volume2 size={18} />
          Hear Letter
        </button>
        <button onClick={clearTraceCanvas}>
          <RefreshCw size={18} />
          Try Again
        </button>
        <button onClick={markDone}>
          <Check size={18} />
          Done
        </button>
        <button onClick={reset}>Restart</button>
      </div>
    </div>
  );
}

function GoodDeedSortGame({ onWin }) {
  const [cards, setCards] = useState(() => shuffle(deedSortItems));
  const [index, setIndex] = useState(0);
  const [doneIds, setDoneIds] = useState([]);
  const [message, setMessage] = useState('Is this a good deed?');
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const current = cards[index];
  const doneCount = doneIds.length;

  const choose = (item, kind) => {
    const correct = item.kind === kind;
    setLastAnswerCorrect(correct);

    if (!correct) {
      setMessage('Almost! Try again.');
      return;
    }

    const nextDone = [...doneIds, item.id];
    setDoneIds(nextDone);
    setMessage(item.kind === 'good' ? 'Great job! That is a good deed.' : 'Yes, try a kinder choice next time.');

    if (nextDone.length === cards.length) {
      setIndex(cards.length);
      onWin();
      return;
    }

    window.setTimeout(() => {
      setIndex((currentIndex) => currentIndex + 1);
      setMessage('Is this a good deed?');
      setLastAnswerCorrect(null);
    }, 650);
  };

  const reset = () => {
    setCards(shuffle(deedSortItems));
    setIndex(0);
    setDoneIds([]);
    setMessage('Is this a good deed?');
    setLastAnswerCorrect(null);
  };

  return (
    <div className="mini-game">
      <p>{message}</p>
      {current ? (
        <div className="sort-focus-layout">
          <article className={`sort-card sort-card-large ${lastAnswerCorrect === true ? 'correct' : ''} ${lastAnswerCorrect === false ? 'incorrect' : ''}`}>
            <DecorIcon type={current.icon} size={46} />
            <strong>{current.text}</strong>
          </article>
          <div className="sort-actions sort-actions-large">
            <button onClick={() => choose(current, 'good')}>Good Deed</button>
            <button onClick={() => choose(current, 'try')}>Try Again</button>
          </div>
        </div>
      ) : (
        <div className="sort-complete-panel">
          <Check size={42} />
          <strong>MashaAllah! All sorted.</strong>
        </div>
      )}
      <div className="game-controls">
        <p>{doneCount}/{deedSortItems.length} sorted</p>
        <button onClick={reset}>Reset</button>
      </div>
    </div>
  );
}

function SalahStepsGame({ onWin }) {
  const [choices, setChoices] = useState([]);
  const [pool, setPool] = useState(() => shuffle(salahSteps));
  const [message, setMessage] = useState('Tap the steps in the right order.');
  const nextStep = salahSteps[choices.length];

  const chooseStep = (step) => {
    if (step !== nextStep) {
      setMessage(`Almost. Look for: ${nextStep}`);
      return;
    }

    const updated = [...choices, step];
    setChoices(updated);
    setPool((current) => current.filter((item) => item !== step));

    if (updated.length === salahSteps.length) {
      setMessage('Salah steps complete. Beautiful remembering!');
      onWin();
    } else {
      setMessage('Correct. Choose the next step.');
    }
  };

  const reset = () => {
    setChoices([]);
    setPool(shuffle(salahSteps));
    setMessage('Tap the steps in the right order.');
  };

  return (
    <div className="mini-game">
      <p>{message}</p>
      <div className="salah-path">
        {salahSteps.map((step, stepIndex) => (
          <span key={step}>{choices[stepIndex] || `${stepIndex + 1}`}</span>
        ))}
      </div>
      <div className="answer-grid">
        {pool.map((step) => (
          <button key={step} onClick={() => chooseStep(step)}>{step}</button>
        ))}
      </div>
      <button onClick={reset}>Restart Steps</button>
    </div>
  );
}

function PuzzleGame({ onWin }) {
  const [sequence, setSequence] = useState([]);
  const [done, setDone] = useState(false);
  const [pieces, setPieces] = useState(() => shuffle(bismillahPuzzle));
  const [message, setMessage] = useState('Build Bismillah in the right order.');
  const next = bismillahPuzzle[sequence.length];

  const choose = (piece) => {
    if (done) return;
    if (piece.id !== next.id) {
      setMessage(`Almost. Look for "${next.hint}" next.`);
      return;
    }
    const updated = [...sequence, piece];
    setSequence(updated);
    setPieces((current) => current.filter((item) => item.id !== piece.id));
    if (updated.length === bismillahPuzzle.length) {
      setDone(true);
      setMessage('Bismillah puzzle complete!');
      onWin();
    } else {
      setMessage('Correct. Choose the next word.');
    }
  };

  const reset = () => {
    setSequence([]);
    setDone(false);
    setPieces(shuffle(bismillahPuzzle));
    setMessage('Build Bismillah in the right order.');
  };

  return (
    <div className="mini-game">
      <p className="quiz-count">Arabic Phrase Puzzle</p>
      <h4 dir="rtl" lang="ar">بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيمِ</h4>
      <p>{message}</p>
      <div className="puzzle-pieces arabic-puzzle-pieces" dir="rtl" lang="ar">
        {pieces.map((piece) => (
          <button key={piece.id} dir="rtl" lang="ar" onClick={() => choose(piece)}>{piece.text}</button>
        ))}
      </div>
      <div className="pattern-strip progress-pattern arabic-progress-pattern" dir="rtl" lang="ar">
        {bismillahPuzzle.map((piece, index) => <span key={piece.id} dir="rtl" lang="ar">{sequence[index]?.text || '•'}</span>)}
      </div>
      <button onClick={reset}>Restart Puzzle</button>
    </div>
  );
}

function TicTacToeGame({ onWin }) {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [message, setMessage] = useState('You are X. Make three in a row!');
  const [finished, setFinished] = useState(false);

  const lines = [
    [0, 1, 2], [3, 4, 5], [6, 7, 8],
    [0, 3, 6], [1, 4, 7], [2, 5, 8],
    [0, 4, 8], [2, 4, 6],
  ];

  const winnerFor = (cells) => {
    const winningLine = lines.find(([a, b, c]) => cells[a] && cells[a] === cells[b] && cells[a] === cells[c]);
    return winningLine ? cells[winningLine[0]] : null;
  };

  const chooseCell = (index) => {
    if (finished || board[index]) return;

    const playerBoard = board.map((cell, cellIndex) => cellIndex === index ? 'X' : cell);
    const playerWinner = winnerFor(playerBoard);
    if (playerWinner) {
      setBoard(playerBoard);
      setFinished(true);
      setMessage('You won! Three Xs in a row.');
      onWin();
      return;
    }

    const openCells = playerBoard.map((cell, cellIndex) => cell ? null : cellIndex).filter((cellIndex) => cellIndex !== null);
    if (openCells.length === 0) {
      setBoard(playerBoard);
      setFinished(true);
      setMessage('Good game! It is a draw.');
      onWin();
      return;
    }

    const winningMove = openCells.find((cellIndex) => winnerFor(playerBoard.map((cell, nextIndex) => nextIndex === cellIndex ? 'O' : cell)) === 'O');
    const blockingMove = openCells.find((cellIndex) => winnerFor(playerBoard.map((cell, nextIndex) => nextIndex === cellIndex ? 'X' : cell)) === 'X');
    const appChoice = winningMove ?? blockingMove ?? (openCells.includes(4) ? 4 : openCells[Math.floor(Math.random() * openCells.length)]);
    const appBoard = playerBoard.map((cell, cellIndex) => cellIndex === appChoice ? 'O' : cell);
    const appWinner = winnerFor(appBoard);

    setBoard(appBoard);
    if (appWinner) {
      setFinished(true);
      setMessage('O won this round. Try again!');
    } else if (appBoard.every(Boolean)) {
      setFinished(true);
      setMessage('Good game! It is a draw.');
      onWin();
    } else {
      setMessage('Nice move. Your turn!');
    }
  };

  const reset = () => {
    setBoard(Array(9).fill(null));
    setMessage('You are X. Make three in a row!');
    setFinished(false);
  };

  return (
    <div className="mini-game">
      <div className="tic-grid" aria-label="X and O board">
        {board.map((cell, index) => (
          <button key={index} className={cell ? `played ${cell.toLowerCase()}` : ''} onClick={() => chooseCell(index)}>
            {cell || ''}
          </button>
        ))}
      </div>
      <div className="game-controls">
        <p>{message}</p>
        <button onClick={reset}>New Round</button>
      </div>
    </div>
  );
}

function shuffle(items) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }
  return shuffled;
}

function getProphetVoiceText(prophet) {
  return prophet.voiceText || prophet.story.replaceAll('ﷺ', 'peace be upon him');
}

function playDhikrVoice(item, reward, message) {
  const text = item.voiceText || item.arabic || item.spoken || item.english || item.label;
  const folder = item.arabic ? 'words' : 'phrases';
  const audioId = item.audioId || item.id;
  return playOliverAudio(`/voice-audio/${folder}/${audioId}-oliver.wav`, reward, message, text);
}

function QuranSection({ onComplete, progress }) {
  return (
    <Section title="Quran Garden" subtitle="Short surahs with safe listen buttons and memorisation tracking.">
      <div className="card-grid">
        {surahs.map((surah) => (
          <LearningCard
            key={surah.id}
            title={<span className="arabic-card-title" dir="rtl" lang="ar">{surah.arabicName}</span>}
            kicker={surah.englishName}
            description={`${surah.translation} ${surah.description}`}
            icon={<DecorIcon type="quran" size={44} />}
            completed={progress.memorisedSurahs.includes(surah.id)}
            actionLabel="Memorised"
            secondaryLabel="Listen"
            secondaryUrl={surah.listenUrl}
            onSecondary={() => onComplete('listenedSurahs', surah.id, null, `Listened to ${surah.englishName}`)}
            onAction={() => onComplete('memorisedSurahs', surah.id, null, `${surah.englishName} memorised!`)}
          />
        ))}
      </div>
    </Section>
  );
}

function ArabicSection({ onComplete, progress }) {
  return (
    <Section title="Arabic Letter Islands" subtitle="Tap Listen to hear the Arabic letter, then mark it learned.">
      <LetterTraceActivity onComplete={onComplete} progress={progress} />
      <div className="letter-grid arabic-letter-grid" dir="rtl">
        {arabicLetters.map((letter) => (
          <article key={letter.id} dir="ltr" className={`letter-card ${progress.completedArabic.includes(letter.id) ? 'completed' : ''}`}>
            <span className="arabic-letter" dir="rtl" lang="ar">{letter.letter}</span>
            <h3>{letter.transliteration}</h3>
            <div className="card-actions">
              <button
                onClick={() => {
                  playOliverAudio(`/voice-audio/letters/${letter.id}-oliver.wav`, () => {}, null, letter.voiceText);
                  onComplete('listenedArabic', letter.id, null, `Listening to ${letter.transliteration}`);
                }}
              >
                <Volume2 size={18} />
                Listen
              </button>
              <button onClick={() => onComplete('completedArabic', letter.id, null, `${letter.transliteration} learned!`)}>
                <Check size={18} />
                Done
              </button>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

function WordsSection({ onComplete, progress }) {
  return (
    <Section title="Beautiful Arabic Words" subtitle="Tap Listen for Arabic voiceover, then mark each word learned.">
      <div className="word-grid">
        {simpleWords.map((word) => {
          const learned = progress.completedWords.includes(word.id);
          return (
            <article key={word.id} className={`word-card ${learned ? 'completed' : ''}`}>
              <span className="word-arabic" dir="rtl" lang="ar">{word.arabic}</span>
              <p className="kicker">{word.english}</p>
              <h3>{word.meaning}</h3>
              <p>{word.when}</p>
              {learned && <span className="badge"><Award size={16} /> Learned</span>}
              <div className="card-actions">
                <button
                  onClick={() => {
                    playDhikrVoice(word, () => {}, null);
                    onComplete('listenedWords', word.id, null, `Listening to ${word.english}`);
                  }}
                >
                  <Volume2 size={18} />
                  Listen
                </button>
                <button onClick={() => onComplete('completedWords', word.id, null, `${word.english} learned!`)}>
                  <Check size={18} />
                  {learned ? 'Learned' : 'I know it'}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </Section>
  );
}

function LetterTraceActivity({ onComplete, progress }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [index, setIndex] = useState(0);
  const current = arabicLetters[index];
  const traceId = `${current.id}-trace`;
  const done = progress.completedTracing?.includes(traceId);

  useEffect(() => {
    clearTrace();
  }, [index]);

  const clearTrace = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 16;
    ctx.strokeStyle = '#1fb8a6';
  };

  const tracePoint = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = event.touches?.[0];
    const clientX = touch ? touch.clientX : event.clientX;
    const clientY = touch ? touch.clientY : event.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvasRef.current.width,
      y: ((clientY - rect.top) / rect.height) * canvasRef.current.height,
    };
  };

  const start = (event) => {
    event.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const point = tracePoint(event);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const point = tracePoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stop = () => {
    drawing.current = false;
  };

  const moveLetter = (direction) => {
    setIndex((currentIndex) => {
      const nextIndex = (currentIndex + direction + arabicLetters.length) % arabicLetters.length;
      return nextIndex;
    });
  };

  return (
    <article className={`trace-card ${done ? 'completed' : ''}`}>
      <div>
        <p className="kicker">Trace Practice</p>
        <h3>Trace {current.transliteration}</h3>
        <p>Use Next and Back to practise every Arabic letter.</p>
        <strong>{index + 1}/{arabicLetters.length}</strong>
      </div>
      <div className="trace-board">
        <span className="trace-guide" dir="rtl" lang="ar">{current.letter}</span>
        <canvas
          ref={canvasRef}
          width="280"
          height="320"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={stop}
          aria-label={`Trace ${current.transliteration} canvas`}
        />
      </div>
      <div className="trace-actions">
        <button onClick={() => moveLetter(-1)}>
          Previous Letter
        </button>
        <button onClick={() => moveLetter(1)}>
          Next Letter
        </button>
        <button onClick={() => playOliverAudio(`/voice-audio/letters/${current.id}-oliver.wav`, () => {}, null, current.voiceText)}>
          <Volume2 size={18} />
          Hear {current.transliteration}
        </button>
        <button onClick={clearTrace}>
          <RefreshCw size={18} />
          Try Again
        </button>
        <button onClick={() => onComplete('completedTracing', traceId, null, `${current.transliteration} traced!`)}>
          <Check size={18} />
          {done ? 'Traced' : 'I traced it'}
        </button>
      </div>
    </article>
  );
}

function ProphetsSection({ onComplete, progress }) {
  const [activeStoryId, setActiveStoryId] = useState(prophets[0].id);
  const [storySpeech, setStorySpeech] = useState({ speaking: false, paused: false, loading: false, source: null });
  const storyAudioRef = useRef(null);
  const activeStory = prophets.find((prophet) => prophet.id === activeStoryId) || prophets[0];

  useEffect(() => {
    return () => {
      stopSpeech();
      if (storyAudioRef.current) {
        storyAudioRef.current.pause();
        storyAudioRef.current.currentTime = 0;
        storyAudioRef.current = null;
      }
    };
  }, []);

  const stopStoryAudio = () => {
    stopSpeech();
    if (storyAudioRef.current) {
      storyAudioRef.current.pause();
      storyAudioRef.current.currentTime = 0;
      storyAudioRef.current = null;
    }
  };

  const openStory = (prophet) => {
    setActiveStoryId(prophet.id);
    trackEvent('select_content', { content_type: 'prophet_story', item_id: prophet.id });
    stopStoryAudio();
    setStorySpeech({ speaking: false, paused: false, loading: false, source: null });
  };

  const readStory = async (prophet) => {
    stopStoryAudio();
    setStorySpeech({ speaking: false, paused: false, loading: true, source: 'typecast' });

    try {
      const audioSrc = prophet.voiceText
        ? await getCachedTypecastAudioUrl(getProphetVoiceText(prophet), `story-${prophet.id}-${stableAudioKey(getProphetVoiceText(prophet))}`)
        : `/story-audio/${prophet.id}-oliver.wav`;
      const audio = new Audio(audioSrc);
      storyAudioRef.current = audio;
      audio.onended = () => setStorySpeech({ speaking: false, paused: false, loading: false, source: null });
      audio.onerror = () => {
        storyAudioRef.current = null;
        setStorySpeech({ speaking: false, paused: false, loading: false, source: null });
      };
      await audio.play();
      setStorySpeech({ speaking: true, paused: false, loading: false, source: 'typecast' });
      onComplete('readProphets', prophet.id, null, `Oliver is reading ${prophet.name.replace('ﷺ', '')}`);
    } catch (error) {
      console.warn('[StoryVoice] Oliver audio unavailable:', error);
      setStorySpeech({ speaking: false, paused: false, loading: false, source: null });
    }
  };

  const pauseStory = () => {
    if (storyAudioRef.current) {
      storyAudioRef.current.pause();
    }
    setStorySpeech((current) => ({ ...current, paused: true }));
  };

  const resumeStory = () => {
    if (storyAudioRef.current) {
      storyAudioRef.current.play().catch((error) => {
        console.warn('[StoryVoice] Oliver audio unavailable:', error);
        setStorySpeech({ speaking: false, paused: false, loading: false, source: null });
      });
    }
    setStorySpeech((current) => ({ ...current, paused: false }));
  };

  const stopStory = () => {
    stopStoryAudio();
    setStorySpeech({ speaking: false, paused: false, loading: false, source: null });
  };

  return (
    <Section title="Prophet Story Lanterns" subtitle="Gentle story cards prepared for future interactive stories.">
      <article className="story-reader">
        <StoryPicture story={activeStory} />
        <div>
          <p className="kicker">Now Reading</p>
          <h3>{activeStory.name}</h3>
          <p>{activeStory.story}</p>
        </div>
        <div className="story-actions">
          <button
            disabled={storySpeech.loading}
            onClick={() => {
              readStory(activeStory);
            }}
          >
            <Volume2 size={18} />
            {storySpeech.loading ? 'Loading Oliver...' : 'Read Aloud'}
          </button>
          {storySpeech.speaking && !storySpeech.paused && (
            <button onClick={pauseStory}>
              <Pause size={18} />
              Pause
            </button>
          )}
          {storySpeech.speaking && storySpeech.paused && (
            <button onClick={resumeStory}>
              <Play size={18} />
              Resume
            </button>
          )}
          {storySpeech.speaking && (
            <button onClick={stopStory}>
              <RefreshCw size={18} />
              Stop
            </button>
          )}
          <button onClick={() => onComplete('completedProphets', activeStory.id, null, `${activeStory.name} story complete!`)}>
          <Check size={18} />
          Complete Story
          </button>
        </div>
      </article>
      <div className="card-grid">
        {prophets.map((prophet) => (
          <article
            key={prophet.id}
            className={`learning-card ${progress.completedProphets.includes(prophet.id) ? 'completed' : ''}`}
            onClick={() => openStory(prophet)}
            role="button"
            tabIndex="0"
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') openStory(prophet);
            }}
          >
            <StoryPicture story={prophet} compact />
            <p className="kicker">Story</p>
            <h3>{prophet.name}</h3>
            <p>{prophet.summary}</p>
            {progress.completedProphets.includes(prophet.id) && <span className="badge"><Award size={16} /> Completed</span>}
            <div className="card-actions">
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  openStory(prophet);
                  readStory(prophet);
                }}
              >
                <Volume2 size={18} />
                Read Story
              </button>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  onComplete('completedProphets', prophet.id, null, `${prophet.name} story complete!`);
                }}
              >
                <Check size={18} />
                {progress.completedProphets.includes(prophet.id) ? 'Completed' : 'Complete'}
              </button>
            </div>
          </article>
        ))}
      </div>
      <ProphetSourcesPanel />
    </Section>
  );
}

function ProphetSourcesPanel() {
  return (
    <details className="source-panel">
      <summary>
        <span>Story sources parents can check</span>
        <small>Quran references and authentic hadith notes</small>
      </summary>
      <p>
        These stories are short, child-friendly summaries based mainly on Quranic accounts. They are not full tafsir or a fatwa. For detailed learning, please check the verses with a trusted teacher or scholar.
      </p>
      <div className="source-grid">
        {prophetStorySources.map((storySource) => (
          <article key={storySource.name} className="source-card">
            <h3>{storySource.name}</h3>
            <div>
              {storySource.sources.map((source) => (
                <a key={`${storySource.name}-${source.label}`} href={source.url} target="_blank" rel="noreferrer">
                  {source.label}
                </a>
              ))}
            </div>
          </article>
        ))}
      </div>
    </details>
  );
}

function KidsAvatar() {
  return (
    <div className="kids-avatar" aria-label="Children ready to learn and play">
      <span className="kid kid-one">
        <span className="kid-face">
          <span className="kid-cheeks" />
          <span className="kid-smile" />
        </span>
      </span>
      <span className="kid kid-two">
        <span className="kid-face">
          <span className="kid-cheeks" />
          <span className="kid-smile" />
        </span>
      </span>
      <span className="avatar-book"><BookOpen size={28} strokeWidth={2.5} /></span>
      <span className="avatar-sparkle"><Star size={22} fill="currentColor" /></span>
    </div>
  );
}

const prophetAnimals = {
  adam: 'rabbit',
  idris: 'cat',
  nuh: 'turtle',
  hud: 'camel',
  salih: 'camel',
  ibrahim: 'lamb',
  lut: 'cat',
  ismail: 'lamb',
  ishaq: 'rabbit',
  yaqub: 'lamb',
  yusuf: 'cow',
  ayyub: 'turtle',
  shuayb: 'monkey',
  musa: 'duck',
  harun: 'lion',
  yunus: 'turtle',
  dawud: 'bird',
  sulayman: 'bird',
  zakariya: 'rabbit',
  yahya: 'duck',
  isa: 'bird',
  muhammad: 'cat',
};

function StoryPicture({ story, compact = false }) {
  const art = story.art || { theme: 'garden', label: `${story.name} story symbol` };
  const animalId = prophetAnimals[story.id] || 'lamb';
  const size = compact ? 68 : 92;

  return (
    <div className={`story-picture story-theme-${art.theme} ${compact ? 'compact' : ''}`} aria-label={`${story.name} symbolic story picture`}>
      <span className="story-main">
        <img src={animalImages[animalId]} alt="" className="story-main-image" style={{ width: size, height: size }} />
      </span>
      {!compact && <strong className="story-art-label">{art.label}</strong>}
    </div>
  );
}

function PrayerSection() {
  return (
    <Section title="How to Pray" subtitle="A simple visual prayer guide for children. Practise with a parent, teacher, or trusted grown-up.">
      <article className="prayer-info-panel salah-overview-panel">
        <p className="kicker">The Five Daily Salahs</p>
        <h3>Prayers through the day</h3>
        <p>These are the main fard rakahs. A parent or teacher can help children learn sunnah prayers as they grow.</p>
        <div className="salah-card-grid">
          {dailySalahs.map((salah) => (
            <div key={salah.id} className="salah-card">
              <DecorIcon type={salah.icon} size={30} />
              <h4>{salah.name}</h4>
              <strong>{salah.time}</strong>
              <p>{salah.rakahs}</p>
              <small>{salah.note}</small>
            </div>
          ))}
        </div>
      </article>

      <article className="instruction-with-image wudu-guide-panel">
        <div className="instruction-copy">
          <p className="kicker">Before Salah</p>
          <h3>How to make wudu</h3>
          <p>Use the picture guide and simple steps to practise with a grown-up nearby.</p>
          <div className="wudu-step-grid" aria-label="Wudu steps">
            {wuduSteps.map((step, index) => (
              <article key={step.id} className="wudu-step-card">
                <DecorIcon type={step.icon} size={28} />
                <p className="kicker">Step {index + 1}</p>
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </article>
            ))}
          </div>
        </div>
        <img
          src="/images/wudu-steps-guide.png"
          alt="Child-friendly visual guide showing the steps of making wudu"
        />
      </article>

      <article className="instruction-with-image salah-movement-panel">
        <div className="instruction-copy">
          <p className="kicker">Salah Movements</p>
          <h3>See the prayer steps</h3>
          <p>Real-life style pictures help children copy the big movements with a grown-up nearby.</p>
          <div className="prayer-step-list prayer-step-grid" aria-label="Prayer steps">
            {prayerGuideSteps.map((step) => (
              <article key={step.id} className="prayer-step-card">
                <DecorIcon type={step.visual} size={28} />
                <p className="kicker">{step.arabic}</p>
                <h3>{step.title}</h3>
                <p>{step.guidance}</p>
                <small>{step.childTip}</small>
              </article>
            ))}
          </div>
        </div>
        <img
          src="/images/prayer-movements-guide.png"
          alt="Child-friendly visual guide showing wudu, standing, takbir, recitation, ruku, sujood, sitting, and salam prayer movements"
        />
      </article>

      <div className="wide-panel prayer-reminder">
        <div>
          <p className="kicker">Kind reminder</p>
          <h3>Learn slowly and lovingly</h3>
          <p>Prayer has details that families and teachers can explain. This guide helps children remember the big order and enjoy practising.</p>
        </div>
        <ShieldCheck size={42} />
      </div>
    </Section>
  );
}

function OfflineMediaNotice({ label = 'Videos and nasheeds need internet. The other learning areas still work offline.' }) {
  return (
    <div className="offline-media-notice" role="status">
      <ShieldCheck size={32} />
      <strong>Please connect to internet</strong>
      <span>{label}</span>
    </div>
  );
}

function ExternalMediaLink({ item, fallbackLabel = 'Watch on YouTube', onFallbackClick }) {
  return (
    <div className="external-media-link">
      <Play size={34} />
      <strong>{item.title}</strong>
      <span>YouTube blocks this video player inside the iOS app. Open this parent-approved video safely in YouTube instead.</span>
      <a className="card-button" href={item.url} target="_blank" rel="noreferrer" onClick={onFallbackClick}>
        <Play size={18} />
        {fallbackLabel}
      </a>
    </div>
  );
}

function EmbeddedYouTube({ item, fallbackLabel = 'Open Safely', onFallbackClick, isOnline = true }) {
  const playerRef = useRef(null);
  const iframeRef = useRef(null);

  if (!isOnline) {
    return <OfflineMediaNotice />;
  }

  if (isIOSNativeRuntime()) {
    return <ExternalMediaLink item={item} fallbackLabel={fallbackLabel === 'Open Safely' ? 'Watch on YouTube' : fallbackLabel} onFallbackClick={onFallbackClick} />;
  }

  const embedUrl = getYouTubeEmbedUrl(item.url);

  if (!embedUrl) {
    return <ExternalMediaLink item={item} fallbackLabel={fallbackLabel} onFallbackClick={onFallbackClick} />;
  }

  const goFullScreen = () => {
    const el = playerRef.current || iframeRef.current;
    if (!el) return;
    const request = el.requestFullscreen || el.webkitRequestFullscreen || el.webkitEnterFullscreen;
    request?.call(el);
  };

  return (
    <div className="youtube-player" ref={playerRef}>
      <iframe
        ref={iframeRef}
        title={item.title}
        src={embedUrl}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
      <button type="button" className="video-fullscreen-button" onClick={goFullScreen} aria-label="Expand video to full screen">
        <Maximize size={18} />
      </button>
    </div>
  );
}

function VideosSection({ isOnline }) {
  return (
    <Section title="Safe Video Hub" subtitle="Direct parent-approved YouTube videos play inside the app with no autoplay. Channel and search pages stay as safe outbound links.">
      <div className={`safety-banner ${isOnline ? '' : 'offline-banner'}`}>
        <ShieldCheck />
        {isOnline
          ? 'No ads, chat, social feeds, or random recommendations inside this app.'
          : 'Please connect to internet to watch videos. Quran, Arabic, stories, prayer, games, and create still work offline.'}
      </div>
      <div className="card-grid">
        {videos.map((video) => (
          <article key={video.id} className="learning-card video-card">
            <span className="card-icon"><DecorIcon type="video" size={44} /></span>
            <p className="kicker">{video.category}</p>
            <h3>{video.title}</h3>
            <p>{video.provider}</p>
            <EmbeddedYouTube item={video} isOnline={isOnline} />
          </article>
        ))}
      </div>
    </Section>
  );
}

function NasheedsSection({ isOnline, progress, toggleItem, onComplete, reward }) {
  return (
    <Section title="Nasheed Meadow" subtitle="Parent-approved YouTube nasheeds now play inside the app when a direct video link is available.">
      {!isOnline && (
        <div className="safety-banner offline-banner">
          <ShieldCheck />
          Please connect to internet to play nasheeds. You can still favourite items and use the other learning areas offline.
        </div>
      )}
      <div className="card-grid">
        {nasheeds.map((nasheed) => {
          const favourite = progress.favouriteNasheeds.includes(nasheed.id);
          const canEmbed = Boolean(getYouTubeEmbedUrl(nasheed.url));
          return (
            <article key={nasheed.id} className="learning-card nasheed-card">
              <span className="card-icon"><DecorIcon type={nasheed.id === 'arabic-alphabet' ? 'arabic' : 'music'} size={44} /></span>
              <p className="kicker">{nasheed.category}</p>
              <h3>{nasheed.title}</h3>
              <p>{nasheed.provider}</p>
              <EmbeddedYouTube
                item={nasheed}
                fallbackLabel="Open Safely"
                isOnline={isOnline}
                onFallbackClick={() => onComplete('listenedNasheeds', nasheed.id, 1, `Opening ${nasheed.title}`)}
              />
              <div className="card-actions">
                {canEmbed && isOnline && (
                  <button onClick={() => onComplete('listenedNasheeds', nasheed.id, 1, `${nasheed.title} listened to!`)}>
                    <Check size={18} />
                    Mark listened
                  </button>
                )}
                <button
                  className={favourite ? 'is-favourite' : ''}
                  onClick={() => {
                    toggleItem('favouriteNasheeds', nasheed.id);
                    reward(favourite ? 'Removed from favourites' : 'Added to favourites');
                  }}
                >
                  <Heart size={18} fill={favourite ? 'currentColor' : 'none'} />
                  Favourite
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </Section>
  );
}

function CreateSection({ reward }) {
  const [selectedColouringPage, setSelectedColouringPage] = useState(colouringPages[0]);

  return (
    <Section title="Create Studio" subtitle="Colour, draw, and tap meaningful sound buttons.">
      <div className="studio-layout">
        <article className="studio-panel colouring-panel">
          <h3><Palette size={22} /> Colouring Pages</h3>
          <div className="colouring-grid">
            {colouringPages.map((page) => (
              <button
                key={page.id}
                className={selectedColouringPage.id === page.id ? 'selected-colouring' : ''}
                onClick={() => {
                  setSelectedColouringPage(page);
                  reward(`${page.title} loaded for colouring`);
                }}
              >
                <ColouringPreview id={page.id} />
                {page.title}
              </button>
            ))}
          </div>
        </article>
        <DrawingCanvas reward={reward} selectedPage={selectedColouringPage} />
        <article className="studio-panel">
          <h3><Mic2 size={22} /> Sound Buttons</h3>
          <div className="sound-grid">
            {soundButtons.map((sound) => (
              <button
                key={sound.id}
                onClick={() => {
                  playDhikrVoice(sound, reward, sound.label);
                }}
              >
                <Volume2 size={18} />
                {sound.label}
              </button>
            ))}
          </div>
        </article>
      </div>
    </Section>
  );
}

function ColouringPreview({ id, canvasOverlay = false }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  const fine = {
    ...common,
    strokeWidth: 3,
  };

  return (
    <svg className={canvasOverlay ? 'colouring-overlay' : 'colouring-preview'} viewBox="0 0 200 140" aria-hidden="true">
      {id === 'kaaba' && (
        <>
          <path d="M20 128h160" {...common} />
          <rect x="50" y="35" width="72" height="93" {...common} />
          <path d="M122 35l28-13v93l-28 13" {...common} />
          <path d="M50 58h72M122 58l28-13" {...common} />
          <path d="M62 46h6M78 46h6M94 46h6M110 46h6" {...fine} />
          <rect x="76" y="98" width="24" height="30" {...common} />
          <path d="M86 98v30" {...fine} />
        </>
      )}
      {id === 'mosque' && (
        <>
          <path d="M18 120h164" {...common} />
          <path d="M44 118V65c0-30 24-51 56-51s56 21 56 51v53H44z" {...common} />
          <path d="M29 118V48M171 118V48" {...common} />
          <path d="M21 48h16M163 48h16" {...common} />
          <path d="M83 118V88c0-11 34-11 34 0v30" {...common} />
          <path d="M73 31c14 15 40 15 54 0" {...fine} />
          <path d="M100 15V4M91 4h18" {...fine} />
          <path d="M62 78h16M122 78h16M59 98h20M121 98h20" {...fine} />
          <path d="M30 62c-10 7-10 18 0 25M170 62c10 7 10 18 0 25" {...fine} />
        </>
      )}
      {id === 'mat' && (
        <>
          <rect x="46" y="12" width="108" height="116" rx="12" {...common} />
          <path d="M64 128V12M136 128V12" {...fine} />
          <path d="M72 90c8-24 48-24 56 0" {...common} />
          <path d="M72 32h56M72 112h56" {...common} />
          <path d="M56 24h12M132 24h12M56 116h12M132 116h12" {...fine} />
          <path d="M82 48h36M78 62h44" {...fine} />
          <path d="M47 38l-18-12M153 38l18-12M47 103l-18 12M153 103l18 12" {...fine} />
        </>
      )}
      {id === 'moon' && (
        <>
          <path d="M116 18c-41 13-58 66-28 98-39-7-68-43-58-82 9-36 50-58 86-16z" {...common} />
          <path d="M151 28l6 18 18 6-18 6-6 18-6-18-18-6 18-6 6-18z" {...common} />
          <path d="M40 105l4 11 12 4-12 4-4 11-4-11-12-4 12-4 4-11zM162 96l3 9 9 3-9 3-3 9-3-9-9-3 9-3 3-9z" {...fine} />
          <circle cx="76" cy="32" r="4" {...fine} />
          <circle cx="132" cy="102" r="3" {...fine} />
        </>
      )}
      {id === 'stars' && (
        <>
          <path d="M58 13l13 30 32 3-24 21 7 31-28-17-28 17 7-31-24-21 32-3 13-30z" {...common} />
          <path d="M143 29l9 21 23 2-17 15 5 22-20-12-20 12 5-22-17-15 23-2 9-21z" {...common} />
          <path d="M105 88l6 13 15 2-11 10 3 15-13-8-13 8 3-15-11-10 15-2 6-13z" {...common} />
          <path d="M33 109l4 9 10 1-8 7 2 10-8-5-9 5 3-10-8-7 10-1 4-9zM172 101l3 8 9 1-7 6 2 9-7-5-8 5 2-9-7-6 9-1 4-8z" {...fine} />
        </>
      )}
      {id === 'letters' && (
        <>
          <rect x="22" y="18" width="156" height="98" rx="14" {...common} />
          <path d="M22 42h156M74 42v74M126 42v74" {...fine} />
          <text x="48" y="90" fontSize="38" textAnchor="middle" fontWeight="800" fill="none" stroke="currentColor" strokeWidth="2.5">ا</text>
          <text x="100" y="90" fontSize="38" textAnchor="middle" fontWeight="800" fill="none" stroke="currentColor" strokeWidth="2.5">ب</text>
          <text x="152" y="90" fontSize="38" textAnchor="middle" fontWeight="800" fill="none" stroke="currentColor" strokeWidth="2.5">ت</text>
          <path d="M38 122h124" {...fine} />
          <circle cx="51" cy="31" r="3" {...fine} />
          <circle cx="149" cy="31" r="3" {...fine} />
        </>
      )}
      {id === 'lantern' && (
        <>
          <path d="M76 18h48M86 18l-13 24h54l-13-24M67 42h66l-11 78H78L67 42z" {...common} />
          <path d="M84 61h32M80 86h40M100 42v78" {...fine} />
          <path d="M88 18c0-17 24-17 24 0" {...common} />
          <path d="M52 57l-16-15M148 57l16-15M53 103l-18 10M147 103l18 10" {...fine} />
          <path d="M91 121c4 10 14 10 18 0" {...fine} />
          <circle cx="100" cy="72" r="8" {...fine} />
        </>
      )}
      {id === 'dates' && (
        <>
          <path d="M42 14C90 24 118 58 130 116" {...common} />
          <path d="M42 14l-18 9M42 14l8-13" {...fine} />
          <path d="M62 40l-8 20M86 66l-8 20M108 92l-7 20" {...fine} />
          <ellipse cx="52" cy="70" rx="15" ry="19" {...common} />
          <ellipse cx="76" cy="96" rx="15" ry="19" {...common} />
          <ellipse cx="98" cy="120" rx="14" ry="17" {...common} />
        </>
      )}
      {id === 'tasbih' && (
        <>
          <circle cx="150" cy="55" r="9" {...common} />
          <circle cx="140" cy="78" r="9" {...common} />
          <circle cx="115" cy="91" r="9" {...common} />
          <circle cx="85" cy="91" r="9" {...common} />
          <circle cx="60" cy="78" r="9" {...common} />
          <circle cx="50" cy="55" r="9" {...common} />
          <circle cx="60" cy="32" r="9" {...common} />
          <circle cx="85" cy="19" r="9" {...common} />
          <circle cx="115" cy="19" r="9" {...common} />
          <circle cx="140" cy="32" r="9" {...common} />
          <circle cx="100" cy="104" r="13" {...common} />
          <path d="M100 117l-12 20M100 117l-4 21M100 117l4 21M100 117l12 20" {...fine} />
          <circle cx="100" cy="117" r="4" {...fine} />
        </>
      )}
    </svg>
  );
}

function DrawingCanvas({ reward, selectedPage }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [colour, setColour] = useState(drawingColours[0]);
  const [erase, setErase] = useState(false);
  const [isBigCanvas, setIsBigCanvas] = useState(false);

  const resetCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 9;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    resetCanvas();
  }, [selectedPage.id]);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = event.touches?.[0];
    const clientX = touch ? touch.clientX : event.clientX;
    const clientY = touch ? touch.clientY : event.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvasRef.current.width,
      y: ((clientY - rect.top) / rect.height) * canvasRef.current.height,
    };
  };

  const start = (event) => {
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const current = point(event);
    isDrawing.current = true;
    ctx.beginPath();
    ctx.moveTo(current.x, current.y);
  };

  const draw = (event) => {
    if (!isDrawing.current) return;
    event.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const current = point(event);
    ctx.strokeStyle = erase ? '#ffffff' : colour;
    ctx.lineWidth = erase ? 22 : 9;
    ctx.lineTo(current.x, current.y);
    ctx.stroke();
  };

  const stop = () => {
    isDrawing.current = false;
  };

  const clear = () => {
    resetCanvas();
    reward('Canvas cleared');
  };

  const save = () => {
    localStorage.setItem('little_muslim_explorer_drawing', canvasRef.current.toDataURL('image/png'));
    reward('Saved on this device only!');
  };

  return (
    <article className={`studio-panel drawing-panel ${isBigCanvas ? 'big-canvas-mode' : ''}`}>
      <div className="drawing-heading">
        <h3><Paintbrush size={22} /> Colouring: {selectedPage.title}</h3>
        <button className="tool-button" onClick={() => setIsBigCanvas((current) => !current)}>
          {isBigCanvas ? 'Normal Size' : 'Big Canvas'}
        </button>
      </div>
      <p className="save-note">Saved drawings stay in this browser on this device. Nothing is sent to a database.</p>
      <div className="canvas-wrap">
        <canvas
          ref={canvasRef}
          width="760"
          height="440"
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
          aria-label={`${selectedPage.title} colouring canvas`}
        />
        <ColouringPreview id={selectedPage.id} canvasOverlay />
      </div>
      <div className="drawing-tools">
        {drawingColours.map((option) => (
          <button
            key={option}
            className={colour === option && !erase ? 'selected' : ''}
            style={{ background: option }}
            onClick={() => {
              setColour(option);
              setErase(false);
            }}
            aria-label={`Use colour ${option}`}
          />
        ))}
        <button className={erase ? 'tool-button selected-tool' : 'tool-button'} onClick={() => setErase(true)}>
          <Eraser size={18} />
          Erase
        </button>
        <button className="tool-button" onClick={clear}>
          <RefreshCw size={18} />
          Clear
        </button>
        <button className="tool-button save" onClick={save}>
          <Check size={18} />
          Save
        </button>
      </div>
    </article>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="learning-section">
      <div className="section-heading">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function LearningCard({ title, kicker, description, icon, completed, actionLabel, secondaryLabel, secondaryUrl, onAction, onSecondary }) {
  return (
    <article className={`learning-card ${completed ? 'completed' : ''}`}>
      <span className="card-icon">{icon}</span>
      <p className="kicker">{kicker}</p>
      <h3>{title}</h3>
      <p>{description}</p>
      {completed && <span className="badge"><Award size={16} /> Completed</span>}
      <div className="card-actions">
        {secondaryLabel && secondaryUrl && (
          <a className="card-button" href={secondaryUrl} target="_blank" rel="noreferrer" onClick={onSecondary}>
            <Volume2 size={18} />
            {secondaryLabel}
          </a>
        )}
        {secondaryLabel && !secondaryUrl && (
          <button onClick={onSecondary}>
            <Volume2 size={18} />
            {secondaryLabel}
          </button>
        )}
        <button onClick={onAction}>
          <Check size={18} />
          {completed ? 'Completed' : actionLabel}
        </button>
      </div>
    </article>
  );
}

export default App;
